import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';

// Safe Navigation Function for React Router v7 compatibility
const safeNavigate = (navigate) => (e, path) => {
  try {
    e?.preventDefault?.();
    navigate(path); // React Router
    setTimeout(() => {
      const target = new URL(path, window.location.origin);
      if (window.location.pathname + window.location.search !== target.pathname + target.search) {
        window.location.href = path; // fallback respetando destino
      }
    }, 120);
  } catch {
    window.location.href = path;
  }
};
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  User, 
  Building2, 
  Users, 
  Package, 
  FileText, 
  Menu, 
  X,
  Eye,
  EyeOff,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Download,
  Mail,
  MessageCircle,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Activity,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Settings,
  Save,
  Loader2,
  ToggleLeft,
  ToggleRight,
  FolderOpen,
  Tag,
  Calculator,
  Copy,
  Send,
  ArrowLeft,
  Trash,
  CalendarDays,
  Percent,
  Globe,
  Upload,
  Lock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { toast } from 'sonner';
import { Switch } from './components/ui/switch';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API Service
class ApiService {
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const response = await axios.post(`${API}/auth/refresh`, {}, {
                headers: { Authorization: `Bearer ${refreshToken}` }
              });
              const { access_token } = response.data;
              localStorage.setItem('access_token', access_token);
              error.config.headers.Authorization = `Bearer ${access_token}`;
              return this.axiosInstance.request(error.config);
            } catch (refreshError) {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              window.location.href = '/login';
            }
          } else {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(email, password) {
    const response = await this.axiosInstance.post('/auth/login', { email, password });
    return response.data;
  }

  async register(data) {
    const response = await this.axiosInstance.post('/auth/register', data);
    return response.data;
  }

  async getMe() {
    const response = await this.axiosInstance.get('/me');
    return response.data;
  }

  async getCompany() {
    const response = await this.axiosInstance.get('/company');
    return response.data;
  }

  async getDashboard(dateRange) {
    const params = dateRange && dateRange !== 'all' ? `?date_range=${dateRange}` : '';
    const response = await this.axiosInstance.get(`/dashboard${params}`);
    return response.data;
  }

  // Client methods
  async getClients(params = {}) {
    const response = await this.axiosInstance.get('/clients', { params });
    return response.data;
  }

  async getClient(id) {
    const response = await this.axiosInstance.get(`/clients/${id}`);
    return response.data;
  }

  async createClient(data) {
    const response = await this.axiosInstance.post('/clients', data);
    return response.data;
  }
  async searchClients(query) {
    const response = await this.axiosInstance.get(`/clients?search=${encodeURIComponent(query || '')}&limit=8&sort_by=business_name&sort_order=1`);
    return response.data;
  }

  async updateClient(id, data) {
    const response = await this.axiosInstance.put(`/clients/${id}`, data);
    return response.data;
  }

  async toggleClientActive(id) {
    const response = await this.axiosInstance.patch(`/clients/${id}/toggle-active`);
    return response.data;
  }

  async deleteClient(id) {
    const response = await this.axiosInstance.delete(`/clients/${id}`);
    return response.data;
  }

  // Category methods
  async getCategories(params = {}) {
    const response = await this.axiosInstance.get('/categories', { params });
    return response.data;
  }

  async createCategory(data) {
    const response = await this.axiosInstance.post('/categories', data);
    return response.data;
  }

  async updateCategory(id, data) {
    const response = await this.axiosInstance.put(`/categories/${id}`, data);
    return response.data;
  }

  async toggleCategoryActive(id) {
    const response = await this.axiosInstance.patch(`/categories/${id}/toggle-active`);
    return response.data;
  }

  async deleteCategory(id) {
    const response = await this.axiosInstance.delete(`/categories/${id}`);
    return response.data;
  }

  // Product methods
  async getProducts(params = {}) {
    const response = await this.axiosInstance.get('/products', { params });
    return response.data;
  }

  async createProduct(data) {
    const response = await this.axiosInstance.post('/products', data);
    return response.data;
  }

  async updateProduct(id, data) {
    const response = await this.axiosInstance.put(`/products/${id}`, data);
    return response.data;
  }

  async toggleProductActive(id) {
    const response = await this.axiosInstance.patch(`/products/${id}/toggle-active`);
    return response.data;
  }

  async deleteProduct(id) {
    const response = await this.axiosInstance.delete(`/products/${id}`);
    return response.data;
  }

  // Quote methods
  async getQuotes(params = {}) {
    const response = await this.axiosInstance.get('/quotes', { params });
    return response.data;
  }

  async getQuote(id) {
    const response = await this.axiosInstance.get(`/quotes/${id}`);
    return response.data;
  }

  async createQuote(data) {
    const response = await this.axiosInstance.post('/quotes', data);
    return response.data;
  }

  async updateQuote(id, data) {
    const response = await this.axiosInstance.put(`/quotes/${id}`, data);
    return response.data;
  }

  async updateQuoteStatus(id, status) {
    const response = await this.axiosInstance.patch(`/quotes/${id}/status`, { status });
    return response.data;
  }

  async duplicateQuote(id) {
    const response = await this.axiosInstance.post(`/quotes/${id}/duplicate`);
    return response.data;
  }
  
  // PDF Methods
  async generateQuotePDF(id) {
    const response = await this.axiosInstance.post(`/quotes/${id}/pdf`);
    return response.data;
  }
  
  async downloadQuotePDF(id) {
    const response = await this.axiosInstance.get(`/quotes/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
  
  async getQuotePDFMeta(id) {
    const response = await this.axiosInstance.get(`/quotes/${id}/pdf/meta`);
    return response.data;
  }
  
  async getQuotePDFUrl(id) {
    // Use the same backend URL that frontend uses for API calls
    return `${BACKEND_URL}/api/quotes/${id}/pdf`;
  }

  async deleteQuote(id) {
    const response = await this.axiosInstance.delete(`/quotes/${id}`);
    return response.data;
  }

  async seedData() {
    const response = await this.axiosInstance.post('/seed');
    return response.data;
  }

  // Email and sharing methods
  async sendQuoteEmail(quoteId, emailData) {
    const response = await this.axiosInstance.post(`/quotes/${quoteId}/send-email`, emailData);
    return response.data;
  }

  async createWhatsAppLink(quoteId, whatsAppData) {
    const response = await this.axiosInstance.post(`/quotes/${quoteId}/whatsapp-link`, whatsAppData);
    return response.data;
  }

  // Public quote methods (no authentication required)
  async getPublicQuote(token) {
    // Add cache-busting headers for public routes
    const response = await axios.get(`${API}/public/quote/${token}`, {
      headers: {
        'Cache-Control': 'no-store, no-cache, must-revalidate',
        'Pragma': 'no-cache'
      },
      params: {
        v: Date.now() // Cache busting
      }
    });
    return response.data;
  }

  async acceptPublicQuote(token, acceptanceData) {
    const response = await axios.post(`${API}/public/quote/${token}/accept`, acceptanceData, {
      headers: {
        'Cache-Control': 'no-store, no-cache, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    return response.data;
  }

  async rejectPublicQuote(token, rejectionData) {
    const response = await axios.post(`${API}/public/quote/${token}/reject`, rejectionData, {
      headers: {
        'Cache-Control': 'no-store, no-cache, must-revalidate', 
        'Pragma': 'no-cache'
      }
    });
    return response.data;
  }

  async downloadPublicQuotePDF(token) {
    const response = await axios.get(`${API}/public/quote/${token}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Dashboard expiring quotes
  async getExpiringQuotes() {
    const response = await this.axiosInstance.get('/dashboard/expiring-quotes');
    return response.data;
  }

  // Extend quote validity
  async extendQuoteValidity(quoteId, extendDays) {
    const response = await this.axiosInstance.patch(`/quotes/${quoteId}/extend-validity?extend_days=${extendDays}`);
    return response.data;
  }

}

const apiService = new ApiService();

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const [userData, companyData] = await Promise.all([
            apiService.getMe(),
            apiService.getCompany()
          ]);
          setUser(userData);
          setCompany(companyData);
        } catch (error) {
          console.warn('Auth error, clearing tokens:', error.message);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []); // Back to simple dependency array

  const login = async (email, password) => {
    const tokens = await apiService.login(email, password);
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    
    const [userData, companyData] = await Promise.all([
      apiService.getMe(),
      apiService.getCompany()
    ]);
    setUser(userData);
    setCompany(companyData);
  };

  const register = async (data) => {
    const tokens = await apiService.register(data);
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    
    const [userData, companyData] = await Promise.all([
      apiService.getMe(),
      apiService.getCompany()
    ]);
    setUser(userData);
    setCompany(companyData);
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setCompany(null);
  };

  const refreshCompany = async () => {
    try {
      const companyData = await apiService.getCompany();
      setCompany(companyData);
    } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, company, login, register, logout, loading, refreshCompany }}>
      {children}
    </AuthContext.Provider>
  );
};

// Breadcrumb Component
const Breadcrumb = ({ items }) => {
  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />}
            <span className={`text-sm ${index === items.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {item}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;
    
    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Anterior
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Siguiente
        </Button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{startItem}</span> a{' '}
            <span className="font-medium">{endItem}</span> de{' '}
            <span className="font-medium">{totalItems}</span> resultados
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">Anterior</span>
              <ChevronRight className="h-5 w-5 rotate-180" aria-hidden="true" />
            </Button>
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    currentPage === page
                      ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                  }`}
                >
                  {page}
                </Button>
              )
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">Siguiente</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Login Schema
const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Contraseña requerida'),
});

const registerSchema = z.object({
  name: z.string().min(2, 'Nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Contraseña debe tener al menos 8 caracteres'),
  confirm_password: z.string(),
  company_name: z.string().min(2, 'Nombre de empresa debe tener al menos 2 caracteres'),
  terms: z.literal(true, { errorMap: () => ({ message: 'Debes aceptar los términos y condiciones' }) }),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Las contraseñas no coinciden',
  path: ['confirm_password'],
});

// Category Schema
const categorySchema = z.object({
  name: z.string().min(2, 'Nombre debe tener al menos 2 caracteres'),
  description: z.string().optional(),
  active: z.boolean().default(true),
});

// Product Schema
const productSchema = z.object({
  category_id: z.string().min(1, 'Categoría requerida'),
  name: z.string().min(2, 'Nombre debe tener al menos 2 caracteres'),
  short_description: z.string().optional(),
  unit: z.string().min(1, 'Unidad requerida'),
  base_price: z.number().min(0, 'Precio debe ser mayor o igual a 0'),
  active: z.boolean().default(true),
});

// Client Schema
const clientSchema = z.object({
  business_name: z.string().min(2, 'Nombre de empresa debe tener al menos 2 caracteres'),
  contact_name: z.string().min(2, 'Nombre de contacto debe tener al menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
  address: z.string().optional(),
  tax_id: z.string().optional(),
  notes: z.string().optional(),
  active: z.boolean().default(true),
});

// Quote Schema
const quoteItemSchema = z.object({
  product_id: z.string().min(1, 'Producto requerido'),
  quantity: z.number().min(0.01, 'Cantidad debe ser mayor a 0'),
  unit_price: z.number().min(0, 'Precio debe ser mayor o igual a 0'),
  line_discount_pct: z.number().min(0).max(100, 'Descuento debe estar entre 0 y 100').default(0),
});

const quoteSchema = z.object({
  client_id: z.string().min(1, 'Cliente requerido'),
  date: z.string().optional(),
  validity_days: z.number().min(1, 'Días de validez deben ser mayor a 0').default(15),
  global_discount_pct: z.number().min(0).max(100, 'Descuento debe estar entre 0 y 100').default(0),
  sales_notes: z.string().optional(),
  items: z.array(quoteItemSchema).min(1, 'Debe agregar al menos un ítem'),
});

// Login Component
const LoginPage = () => {
  const { login, register: authRegister } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  
  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' }
  });

  const registerForm = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', password: '', confirm_password: '', company_name: '', terms: false }
  });

  const onLogin = async (data) => {
    try {
      await login(data.email, data.password);
      toast.success('¡Bienvenido a Presuply!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Credenciales inválidas');
    }
  };

  const onRegister = async (data) => {
    try {
      const { confirm_password, terms, ...registerData } = data;
      await authRegister(registerData);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al crear la cuenta');
    }
  };

  const seedDemo = async () => {
    try {
      await apiService.seedData();
      toast.success('Datos de demostración creados');
    } catch (error) {
      toast.error('Error al crear datos de demostración');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Presuply</h2>
          <p className="mt-2 text-sm text-gray-600">
            Cotiza en segundos. Cierra más rápido.
          </p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-center">
              {isRegister ? 'Crear cuenta' : 'Iniciar sesión'}
            </CardTitle>
            <CardDescription className="text-center">
              {isRegister 
                ? 'Crea tu cuenta gratis y envía tu primera cotización hoy'
                : 'Ingresa a tu cuenta de Presuply'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isRegister ? (
              <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@empresa.com"
                    {...loginForm.register('email')}
                    className="h-11"
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...loginForm.register('password')}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-sm text-red-600">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                  Iniciar sesión
                </Button>
              </form>
            ) : (
              <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre</Label>
                    <Input
                      id="name"
                      placeholder="Juan Pérez"
                      {...registerForm.register('name')}
                      className="h-11"
                    />
                    {registerForm.formState.errors.name && (
                      <p className="text-sm text-red-600">{registerForm.formState.errors.name.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="tu@empresa.com"
                      {...registerForm.register('email')}
                      className="h-11"
                    />
                    {registerForm.formState.errors.email && (
                      <p className="text-sm text-red-600">{registerForm.formState.errors.email.message}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nombre de la empresa</Label>
                  <Input
                    id="company_name"
                    placeholder="Mi Empresa S.A. de C.V."
                    {...registerForm.register('company_name')}
                    className="h-11"
                  />
                  {registerForm.formState.errors.company_name && (
                    <p className="text-sm text-red-600">{registerForm.formState.errors.company_name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...registerForm.register('password')}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {registerForm.formState.errors.password && (
                    <p className="text-sm text-red-600">{registerForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...registerForm.register('confirm_password')}
                    className="h-11"
                    data-testid="confirm-password-input"
                  />
                  {registerForm.formState.errors.confirm_password && (
                    <p className="text-sm text-red-600">{registerForm.formState.errors.confirm_password.message}</p>
                  )}
                </div>
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="terms"
                    {...registerForm.register('terms')}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600">
                    Acepto los <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">términos y condiciones</a>
                  </label>
                </div>
                {registerForm.formState.errors.terms && (
                  <p className="text-sm text-red-600">{registerForm.formState.errors.terms.message}</p>
                )}
                <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                  Crear cuenta
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                {isRegister 
                  ? '¿Ya tienes cuenta? Iniciar sesión'
                  : '¿No tienes cuenta? Registrarse'
                }
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Layout Component
const DashboardLayout = ({ children }) => {
  const { user, company, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Activity, current: location.pathname === '/dashboard' },
    { name: 'Clientes', href: '/clients', icon: Users, current: location.pathname === '/clients' },
    { 
      name: 'Catálogo', 
      icon: Package, 
      current: location.pathname.startsWith('/catalog'),
      children: [
        { name: 'Categorías', href: '/catalog/categories', current: location.pathname === '/catalog/categories' },
        { name: 'Productos', href: '/catalog/products', current: location.pathname === '/catalog/products' }
      ]
    },
    { name: 'Cotizaciones', href: '/quotes', icon: FileText, current: location.pathname.startsWith('/quotes') },
    { name: 'Ajustes', href: '/settings', icon: Settings, current: location.pathname.startsWith('/settings') },
  ];

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent navigation={navigation} company={company} onNavigate={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigation={navigation} company={company} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top header */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow-sm border-b border-gray-200">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-end items-center">
            <div className="ml-4 flex items-center md:ml-6 space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                <div className="text-xs text-gray-500">{user?.role}</div>
              </div>
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-600 text-white text-sm">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    onClick={logout}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Salir
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Sidebar Content
const SidebarContent = ({ navigation, company, onNavigate }) => {
  const navigate = useNavigate();
  const handleSafeNavigate = safeNavigate(navigate);
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  useEffect(() => {
    // Auto-expand catalog if we're on a catalog page
    if (location.pathname.startsWith('/catalog')) {
      setExpandedItems(prev => ({ ...prev, 'Catálogo': true }));
    }
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Enhanced Logo Section */}
      <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gradient-to-r from-blue-600 to-blue-700 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <FileText className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-white">
            <div className="text-lg font-bold tracking-tight">Presuply</div>
            <div className="text-xs text-blue-100 opacity-90">Gestión de Cotizaciones</div>
          </div>
        </div>
      </div>

      {/* Enhanced Company info */}
      {company && (
        <div className="px-4 py-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="relative" style={{ width: '44px', height: '44px', minWidth: '44px', overflow: 'visible', flexShrink: 0 }}>
              {company.logo_url ? (
                <img 
                  src={company.logo_url.startsWith('http') ? company.logo_url : `${apiService.axiosInstance.defaults.baseURL}${company.logo_url.startsWith('/') ? company.logo_url : '/' + company.logo_url}`}
                  alt={`${company.name} logo`}
                  className="border border-gray-200"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px', backgroundColor: '#f9fafb' }}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 items-center justify-center border border-gray-200" style={{ width: '44px', height: '44px', borderRadius: '6px', display: company.logo_url ? 'none' : 'flex' }}>
                <span className="text-sm font-bold text-blue-600">{(company.name || 'E').substring(0, 2).toUpperCase()}</span>
              </div>
              <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-400 border-2 border-white rounded-full"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {company.name}
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span className="flex items-center space-x-1">
                  <DollarSign className="h-3 w-3" />
                  <span>{company.currency}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedItems[item.name];
          
          return (
            <div key={item.name}>
              <button
                onClick={(e) => {
                  if (hasChildren) {
                    toggleExpanded(item.name);
                  } else {
                    handleSafeNavigate(e, item.href);
                    onNavigate?.();
                  }
                }}
                className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  item.current
                    ? 'bg-blue-100 text-blue-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon
                  className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    item.current ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                />
                <span className="flex-1 text-left">{item.name}</span>
                {hasChildren && (
                  <ChevronRight 
                    className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                )}
              </button>
              
              {hasChildren && isExpanded && (
                <div className="ml-8 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <button
                      key={child.name}
                      onClick={() => { navigate(child.href); onNavigate?.(); }}
                      className={`w-full group flex items-center px-2 py-2 text-sm rounded-md transition-colors ${
                        child.current
                          ? 'bg-blue-100 text-blue-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

// Client Modal Component (Reusable for quick create)
const ClientModal = ({ isOpen, onClose, client, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      business_name: client?.business_name || '',
      contact_name: client?.contact_name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      address: client?.address || '',
      tax_id: client?.tax_id || '',
      notes: client?.notes || '',
      active: client?.active ?? true,
    }
  });

  useEffect(() => {
    if (client) {
      form.reset({
        business_name: client.business_name || '',
        contact_name: client.contact_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        tax_id: client.tax_id || '',
        notes: client.notes || '',
        active: client.active ?? true,
      });
    } else {
      form.reset({
        business_name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        tax_id: '',
        notes: '',
        active: true,
      });
    }
  }, [client, form]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let savedClient;
      if (client) {
        savedClient = await apiService.updateClient(client.id, data);
        toast.success('Cliente actualizado exitosamente');
      } else {
        savedClient = await apiService.createClient(data);
        toast.success('Cliente creado exitosamente');
      }
      onSave(savedClient);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {client ? 'Editar Cliente' : 'Crear Cliente Rápido'}
          </DialogTitle>
          <DialogDescription>
            {client 
              ? 'Actualiza los datos del cliente'
              : 'Registra un nuevo cliente rápidamente'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="business_name">Nombre de la Empresa *</Label>
            <Input
              id="business_name"
              placeholder="Ej: TechCorp S.A. de C.V."
              {...form.register('business_name')}
            />
            {form.formState.errors.business_name && (
              <p className="text-sm text-red-600">{form.formState.errors.business_name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Nombre de Contacto *</Label>
              <Input
                id="contact_name"
                placeholder="Juan Pérez"
                {...form.register('contact_name')}
              />
              {form.formState.errors.contact_name && (
                <p className="text-sm text-red-600">{form.formState.errors.contact_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="juan@techcorp.com"
                {...form.register('email')}
              />
              {form.formState.errors.email && (
                <p className="text-sm text-red-600">{form.formState.errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                placeholder="+52 55 1234 5678"
                {...form.register('phone')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">RFC</Label>
              <Input
                id="tax_id"
                placeholder="ABCD123456789"
                {...form.register('tax_id')}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {client ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Quote Totals Component
const QuoteTotals = ({ items, globalDiscountPct, company }) => {
  const subtotal = items.reduce((sum, item) => {
    const lineAmount = item.quantity * item.unit_price * (1 - (item.line_discount_pct || 0) / 100);
    return sum + lineAmount;
  }, 0);

  const globalDiscountAmount = subtotal * (globalDiscountPct || 0) / 100;
  const baseImponible = subtotal - globalDiscountAmount;
  const ivaAmount = company?.iva_enabled ? baseImponible * (company.iva_pct || 0) / 100 : 0;
  const total = baseImponible + ivaAmount;

  return (
    <Card className="bg-white border-l-4 border-l-blue-600">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center">
          <Calculator className="h-5 w-5 mr-2" />
          Totales
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Subtotal:</span>
          <span>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
        </div>
        {globalDiscountPct > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>Descuento Global ({globalDiscountPct}%):</span>
            <span>-${globalDiscountAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span>Base Imponible:</span>
          <span>${baseImponible.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
        </div>
        {company?.iva_enabled && (
          <div className="flex justify-between text-sm">
            <span>IVA ({company.iva_pct}%):</span>
            <span>${ivaAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="border-t pt-2 mt-2">
          <div className="flex justify-between text-lg font-semibold">
            <span>Total:</span>
            <span className="text-blue-600">
              ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {company?.currency || 'MXN'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Quote Form Component
const QuoteForm = ({ quote, isEditing = false }) => {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  
  // Viewport detection for conditional rendering (prevents duplicate react-hook-form registrations)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Client search typeahead states
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Check if quote items can be edited
  // New quotes: always editable
  // Existing quotes: only editable if BORRADOR status
  const canEditItems = !isEditing || (quote?.status === 'BORRADOR');
  
  // Independent state management per item - using stable tempId instead of index
  const [itemCategories, setItemCategories] = useState({}); // {tempId: categoryId}
  const [itemProducts, setItemProducts] = useState({}); // {tempId: [products]}

  // Generate stable tempId for new items
  const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  const form = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      client_id: '',
      date: new Date().toISOString().split('T')[0],
      validity_days: 15,
      global_discount_pct: 0,
      sales_notes: '',
      items: [{ product_id: '', quantity: 1, unit_price: 0, line_discount_pct: 0, tempId: generateTempId() }]
    }
  });

  // Initialize form with quote data only when editing and quote is provided
  useEffect(() => {
    if (isEditing && quote && quote.items) {
      // Add tempIds to existing items if they don't have them
      const itemsWithTempIds = quote.items.map(item => ({
        ...item,
        tempId: item.tempId || item.id || generateTempId()
      }));

      form.reset({
        client_id: quote.client_id || '',
        date: quote.date ? new Date(quote.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        validity_days: quote.validity_days || 15,
        global_discount_pct: quote.global_discount_pct || 0,
        sales_notes: quote.sales_notes || '',
        items: itemsWithTempIds
      });

      // Initialize category states for existing items
      const initializeItemStates = async () => {
        console.log('🔄 Starting initialization of item states...');
        const newItemCategories = {};
        const newItemProducts = {};

        for (let i = 0; i < itemsWithTempIds.length; i++) {
          const item = itemsWithTempIds[i];
          const tempId = item.tempId;

          console.log(`🔄 Processing item ${i+1}/${itemsWithTempIds.length} with product_id: ${item.product_id}`);

          if (item.product_id) {
            try {
              // Get all products first
              const productsResponse = await apiService.getProducts({ active: true });
              const allProducts = productsResponse.items || productsResponse;
              
              // Find the specific product
              const product = allProducts.find(p => p.id === item.product_id);
              
              if (product && product.category_id) {
                console.log(`✅ Found product "${product.name}" in category ${product.category_id}`);
                
                // Set category for this item
                newItemCategories[tempId] = product.category_id;
                
                // Load all products for this category
                const categoryProductsResponse = await apiService.getProducts({ 
                  category_id: product.category_id, 
                  active: true 
                });
                const categoryProducts = categoryProductsResponse.items || categoryProductsResponse;
                newItemProducts[tempId] = categoryProducts;
                
                console.log(`✅ Loaded ${categoryProducts.length} products for category ${product.category_id}`);
              } else {
                console.warn(`⚠️ Product ${item.product_id} not found or has no category`);
                // Initialize empty states for this item
                newItemCategories[tempId] = '';
                newItemProducts[tempId] = [];
              }
            } catch (error) {
              console.error(`❌ Error loading product/category for item ${i+1}:`, error);
              // Initialize empty states for this item
              newItemCategories[tempId] = '';
              newItemProducts[tempId] = [];
            }
          } else {
            console.log(`⚠️ Item ${i+1} has no product_id, initializing empty`);
            // Initialize empty states for items without products
            newItemCategories[tempId] = '';
            newItemProducts[tempId] = [];
          }
        }

        console.log('🔄 Setting item categories:', Object.keys(newItemCategories).length);
        console.log('🔄 Setting item products:', Object.keys(newItemProducts).length);
        
        setItemCategories(newItemCategories);
        setItemProducts(newItemProducts);
        console.log('✅ Initialized categories and products for', itemsWithTempIds.length, 'items');
      };

      initializeItemStates();
      console.log('🔄 Form initialized with quote data:', itemsWithTempIds.length, 'items');
    }
  }, [isEditing, quote]); // Removed 'form' from dependencies to prevent infinite loop

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsResponse, categoriesResponse] = await Promise.all([
          apiService.getClients({ active: true }),
          apiService.getCategories({ active: true })
        ]);
        const clientsData = clientsResponse.items || clientsResponse;
        setClients(clientsData);
        setCategories(categoriesResponse.items || categoriesResponse);
        
        // Initialize selected client if editing
        if (isEditing && quote?.client_id) {
          const foundClient = clientsData.find(client => client.id === quote.client_id);
          if (foundClient) {
            setSelectedClient(foundClient);
            setClientSearch(`${foundClient.business_name} - ${foundClient.contact_name}`);
          }
        }
      } catch (error) {
        toast.error('Error al cargar datos');
      }
    };
    loadData();
  }, [isEditing, quote?.client_id]);

  const refreshCategories = async () => {
    try {
      const response = await apiService.getCategories({ active: true });
      setCategories(response.items || response);
    } catch (error) {
      toast.error('Error al actualizar categorías');
    }
  };

  const refreshClients = async () => {
    try {
      const response = await apiService.getClients({ active: true });
      setClients(response.items || response);
    } catch (error) {
      toast.error('Error al actualizar clientes');
    }
  };

  // Load products for specific item when its category changes
  const loadProductsForItem = async (tempId, categoryId) => {
    try {
      const response = await apiService.getProducts({ 
        category_id: categoryId, 
        active: true 
      });
      setItemProducts(prev => ({
        ...prev,
        [tempId]: response.items || response
      }));
    } catch (error) {
      console.error('Error loading products for item:', error);
      toast.error('Error al cargar productos');
    }
  };
  
  // Handle category selection for specific item
  const onCategorySelect = (tempId, categoryId) => {
    // Update category for this item
    setItemCategories(prev => ({
      ...prev,
      [tempId]: categoryId
    }));
    
    // Find the field index for this tempId to update form values
    const fieldIndex = fields.findIndex(field => field.tempId === tempId);
    if (fieldIndex !== -1) {
      // Reset product selection for this item
      form.setValue(`items.${fieldIndex}.product_id`, '');
      form.setValue(`items.${fieldIndex}.unit_price`, 0);
    }
    
    // Load products for this category
    if (categoryId) {
      loadProductsForItem(tempId, categoryId);
    } else {
      // Clear products for this item if no category selected
      setItemProducts(prev => ({
        ...prev,
        [tempId]: []
      }));
    }
  };

  const watchedItems = form.watch('items');
  const watchedGlobalDiscount = form.watch('global_discount_pct');

  const onProductSelect = (tempId, productId) => {
    // Find the field index for this tempId
    const fieldIndex = fields.findIndex(field => field.tempId === tempId);
    if (fieldIndex === -1) return;
    
    if (!productId) {
      form.setValue(`items.${fieldIndex}.unit_price`, 0);
      return;
    }
    
    // Get products for this specific item
    const itemProductList = itemProducts[tempId] || [];
    const product = itemProductList.find(p => p.id === productId);
    
    if (product) {
      form.setValue(`items.${fieldIndex}.unit_price`, product.base_price);
      // Force re-render to update totals
      form.trigger(`items.${fieldIndex}.unit_price`);
    } else {
      console.warn('Product not found in item products:', productId);
      form.setValue(`items.${fieldIndex}.unit_price`, 0);
    }
  };

  const addItem = () => {
    const newTempId = generateTempId();
    console.log('➕ Adding new item with tempId:', newTempId);
    
    append({ 
      product_id: '', 
      quantity: 1, 
      unit_price: 0, 
      line_discount_pct: 0,
      tempId: newTempId 
    });
    
    // Initialize states for new item
    setItemCategories(prev => ({ ...prev, [newTempId]: '' }));
    setItemProducts(prev => ({ ...prev, [newTempId]: [] }));
    
    console.log('✅ New item added. Total items now:', fields.length + 1);
  };

  const duplicateItem = (tempId) => {
    const item = fields.find(field => field.tempId === tempId);
    if (!item) return;
    
    const newTempId = generateTempId();
    console.log('📋 Duplicating item with tempId:', tempId, '→', newTempId);
    
    append({ 
      ...item, 
      tempId: newTempId 
    });
    
    // Copy states from original item
    setItemCategories(prev => ({ 
      ...prev, 
      [newTempId]: prev[tempId] || '' 
    }));
    setItemProducts(prev => ({ 
      ...prev, 
      [newTempId]: prev[tempId] || [] 
    }));
  };

  const removeItem = (tempId) => {
    const fieldIndex = fields.findIndex(field => field.tempId === tempId);
    if (fieldIndex === -1) return;
    
    if (fields.length > 1) {
      console.log('🗑️ Removing item with tempId:', tempId, 'at index:', fieldIndex);
      
      remove(fieldIndex);
      
      // Clean up states for removed item
      setItemCategories(prev => {
        const newCategories = { ...prev };
        delete newCategories[tempId];
        return newCategories;
      });
      
      setItemProducts(prev => {
        const newProducts = { ...prev };
        delete newProducts[tempId];
        return newProducts;
      });
      
      console.log('✅ Item removed. Total items now:', fields.length - 1);
    } else {
      toast.error('Debe mantener al menos un ítem');
    }
  };

  const onSubmit = async (data) => {
    console.log('💾 Submitting quote with items:', data.items.length);
    
    setLoading(true);
    try {
      // Clean up tempIds before sending to backend
      const cleanedData = {
        ...data,
        date: new Date(data.date).toISOString(),
        items: data.items.map(item => {
          const { tempId, ...cleanItem } = item;
          return cleanItem;
        })
      };
      
      console.log('🔄 Cleaned data for backend:', cleanedData.items.length, 'items');
      
      let savedQuote;
      if (isEditing && quote) {
        savedQuote = await apiService.updateQuote(quote.id, cleanedData);
        toast.success('Cotización actualizada exitosamente');
        console.log('✅ Quote updated successfully with', cleanedData.items.length, 'items');
      } else {
        savedQuote = await apiService.createQuote(cleanedData);
        toast.success('Cotización creada exitosamente');
        console.log('✅ Quote created successfully with', cleanedData.items.length, 'items');
      }
      navigate(`/quotes/${savedQuote.id}`);
    } catch (error) {
      console.error('❌ Error saving quote:', error);
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail;
      
      let errorMessage = 'Error al guardar la cotización';
      
      if (status === 400 && errorDetail) {
        if (errorDetail.includes('Accepted quotes')) {
          errorMessage = 'Esta cotización ya fue aceptada y no puede ser modificada. Solo se pueden editar cotizaciones en estado BORRADOR.';
        } else if (errorDetail.includes('Missing required')) {
          errorMessage = 'Faltan datos requeridos. Verifica que todos los campos estén completos.';
        } else {
          errorMessage = `Error de validación: ${errorDetail}`;
        }
      } else if (status === 405) {
        errorMessage = 'Método no permitido. La cotización no puede ser actualizada en su estado actual.';
      } else if (status === 404) {
        errorMessage = 'Cotización no encontrada.';
      } else if (errorDetail) {
        errorMessage = errorDetail;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onClientCreated = (newClient) => {
    setClients(prev => [...prev, newClient]);
    handleClientSelect(newClient);
  };
  // Client search typeahead functions
  const searchClients = async (query) => {
    try {
      const response = await apiService.searchClients(query || '');
      const clientsData = response.items || response;
      setClientSuggestions(clientsData);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSuggestions([]);
    }
  };
  
  // Debounced search with 300ms delay — fetch only, don't open dropdown on mount
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchClients(clientSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [clientSearch]);
  
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch(`${client.business_name} - ${client.contact_name}`);
    form.setValue('client_id', client.id);
    setShowClientDropdown(false);
    setClientSuggestions([]); // Clear suggestions to prevent confusion
  };
  
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setShowClientDropdown(true);
    
    // Clear selection if user is typing
    if (selectedClient && value !== `${selectedClient.business_name} - ${selectedClient.contact_name}`) {
      setSelectedClient(null);
      form.setValue('client_id', '');
    }
  };

  // Close dropdown when clicking outside
  const clientDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Primero configura tu catálogo</h3>
        <p className="text-gray-500 mb-4">
          Para crear cotizaciones necesitas al menos una categoría y un producto. Es rápido, solo toma un par de minutos.
        </p>
        {user?.role === 'ADMIN' && (
          <Button onClick={() => navigate('/catalog/categories')} className="bg-blue-600 hover:bg-blue-700">
            Ir al Catálogo
          </Button>
        )}
        <div className="mt-4">
          <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-700">
            ← Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost" 
            onClick={() => navigate('/quotes')}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? `Editar Cotización #${quote?.seq}` : 'Nueva Cotización'}
            </h1>
            <Breadcrumb items={['Cotizaciones', isEditing ? 'Editar' : 'Nueva']} />
          </div>
        </div>
      </div>

      <form id="quote-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-20 md:pb-0">
        {/* Header Section */}
        <Card>
          <CardHeader>
            <CardTitle>Información General</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">Cliente *</Label>
                <div className="flex space-x-2">
                  <div className="flex-1 relative" ref={clientDropdownRef}>
                    <Input
                      type="text"
                      placeholder="Busca cliente por nombre, empresa o email..."
                      value={clientSearch}
                      onChange={handleClientSearchChange}
                      onFocus={async () => { await searchClients(''); setShowClientDropdown(true); }}
                      className="w-full pr-9"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={async () => {
                        if (showClientDropdown) {
                          setShowClientDropdown(false);
                        } else {
                          await searchClients('');
                          setShowClientDropdown(true);
                        }
                      }}
                      className="absolute right-0 inset-y-0 flex items-center px-2 text-gray-400 hover:text-gray-600"
                      data-testid="client-dropdown-toggle"
                    >
                      {showClientDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    
                    {/* Typeahead Dropdown */}
                    {showClientDropdown && clientSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-[340px] overflow-y-auto">
                        {clientSuggestions.slice(0, 8).map((client) => (
                          <div
                            key={client.id}
                            onClick={() => handleClientSelect(client)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            style={{ minHeight: '40px' }} // Touch-friendly
                          >
                            <div className="font-medium text-gray-900">{client.business_name}</div>
                            <div className="text-sm text-gray-600">{client.contact_name}</div>
                            {client.email && <div className="text-xs text-gray-500">{client.email}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* No results message */}
                    {showClientDropdown && clientSuggestions.length === 0 && clientSearch.length > 2 && !selectedClient && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 p-4">
                        <div className="text-center">
                          <p className="text-gray-500 mb-2">No se encontraron clientes</p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              // Pre-fill modal with search term
                              setClientModalOpen(true);
                            }}
                            className="text-sm"
                          >
                            Crear cliente rápido
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setClientModalOpen(true)}
                    title="Crear cliente"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {form.formState.errors.client_id && (
                  <p className="text-sm text-red-600">{form.formState.errors.client_id.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Fecha</Label>
                  <Input
                    id="date"
                    type="date"
                    {...form.register('date')}
                  />
                  {form.formState.errors.date && (
                    <p className="text-sm text-red-600">{form.formState.errors.date.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="validity_days">Días de Validez</Label>
                  <Input
                    id="validity_days"
                    type="number"
                    min="1"
                    {...form.register('validity_days', { valueAsNumber: true })}
                  />
                  {form.formState.errors.validity_days && (
                    <p className="text-sm text-red-600">{form.formState.errors.validity_days.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="global_discount_pct">Descuento Global (%)</Label>
                <Input
                  id="global_discount_pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  {...form.register('global_discount_pct', { valueAsNumber: true })}
                />
                {form.formState.errors.global_discount_pct && (
                  <p className="text-sm text-red-600">{form.formState.errors.global_discount_pct.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sales_notes">Notas de Venta</Label>
                <Textarea
                  id="sales_notes"
                  placeholder="Notas adicionales para la cotización"
                  {...form.register('sales_notes')}
                  rows={2}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Ítems de la Cotización</CardTitle>
              <Button type="button" onClick={addItem} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Ítem
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {fields.map((field, index) => {
                const tempId = field.tempId || field.id;
                return (
                <div key={tempId}>
                  {/* Desktop Item Layout - conditionally rendered to avoid duplicate react-hook-form registrations */}
                  {!isMobile && <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Ítem {index + 1}</h4>
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateItem(tempId)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(tempId)}
                          disabled={fields.length === 1}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Categoría</Label>
                        <div className="flex space-x-2">
                          <Select 
                            value={itemCategories[tempId] || ''}
                            onValueChange={(value) => onCategorySelect(tempId, value)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Selecciona categoría" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((category) => (
                                <SelectItem key={category.id} value={category.id}>
                                  {category.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {user?.role === 'ADMIN' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setCategoryModalOpen(true)}
                              title="Crear categoría rápida"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Producto *</Label>
                        <div className="flex space-x-2">
                          <Select 
                            value={form.watch(`items.${index}.product_id`) || ''}
                            onValueChange={(value) => {
                              form.setValue(`items.${index}.product_id`, value);
                              onProductSelect(tempId, value);
                            }}
                            disabled={!itemCategories[tempId] || (itemProducts[tempId] || []).length === 0}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder={
                                !itemCategories[tempId] 
                                  ? "Selecciona categoría primero" 
                                  : (itemProducts[tempId] || []).length === 0 
                                    ? "No hay productos activos — ir a Catálogo (ADMIN)"
                                    : "Selecciona producto"
                              } />
                            </SelectTrigger>
                            <SelectContent>
                              {(itemProducts[tempId] || []).length > 0 ? (
                                (itemProducts[tempId] || []).map((product) => (
                                  <SelectItem key={product.id} value={product.id}>
                                    {product.name} - ${product.base_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                  </SelectItem>
                                ))
                              ) : (
                                <div className="p-2 text-sm text-gray-500 text-center">
                                  No hay productos activos en esta categoría
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          {user?.role === 'ADMIN' && itemCategories[tempId] && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setProductModalOpen(true)}
                              title="Crear producto rápido"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {form.formState.errors.items?.[index]?.product_id && (
                          <p className="text-sm text-red-600">
                            {form.formState.errors.items[index].product_id.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Cantidad *</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          disabled={!canEditItems}
                          {...form.register(`items.${index}.quantity`, { 
                            valueAsNumber: true,
                            validate: (value) => {
                              if (!value || value < 1) return "Cantidad mínima: 1";
                              return true;
                            }
                          })}
                        />
                        {form.formState.errors.items?.[index]?.quantity && (
                          <p className="text-sm text-red-600">
                            {form.formState.errors.items[index].quantity.message}
                          </p>
                        )}
                        {!canEditItems && (
                          <p className="text-xs text-gray-500">Campo bloqueado - cotización {quote?.status}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Precio Unitario *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          {...form.register(`items.${index}.unit_price`, { valueAsNumber: true })}
                        />
                        {form.formState.errors.items?.[index]?.unit_price && (
                          <p className="text-sm text-red-600">
                            {form.formState.errors.items[index].unit_price.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Descuento (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          disabled={!canEditItems}
                          {...form.register(`items.${index}.line_discount_pct`, { 
                            valueAsNumber: true
                          })}
                        />
                        {form.formState.errors.items?.[index]?.line_discount_pct && (
                          <p className="text-sm text-red-600">
                            {form.formState.errors.items[index].line_discount_pct.message}
                          </p>
                        )}
                        {!canEditItems && (
                          <p className="text-xs text-gray-500">Campo bloqueado - cotización {quote?.status}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Total Línea</Label>
                        <div className="h-10 px-3 py-2 bg-gray-50 border rounded-md flex items-center text-sm">
                          ${((watchedItems[index]?.quantity || 0) * 
                             (watchedItems[index]?.unit_price || 0) * 
                             (1 - (watchedItems[index]?.line_discount_pct || 0) / 100)
                          ).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>}

                  {/* Mobile Item Layout - conditionally rendered to avoid duplicate react-hook-form registrations */}
                  {isMobile && <div className="p-4 space-y-4 relative">
                    {/* Mobile Actions - Top Right */}
                    <div className="absolute top-3 right-3 flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-10 h-10 p-0"
                        onClick={() => duplicateItem(tempId)}
                        title="Duplicar ítem"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-10 h-10 p-0"
                        onClick={() => removeItem(tempId)}
                        disabled={fields.length === 1}
                        title="Eliminar ítem"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Mobile Header */}
                    <div className="mb-2">
                      <h4 className="font-semibold text-base">Ítem {index + 1}</h4>
                    </div>

                    {/* Categoría - full width */}
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold">Categoría</Label>
                      <Select 
                        value={itemCategories[tempId] || ''}
                        onValueChange={(value) => onCategorySelect(tempId, value)}
                      >
                        <SelectTrigger className="w-full h-11">
                          <SelectValue placeholder="Selecciona categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Producto - full width */}
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold">Producto *</Label>
                      <Select 
                        value={form.watch(`items.${index}.product_id`) || ''}
                        onValueChange={(value) => {
                          form.setValue(`items.${index}.product_id`, value);
                          onProductSelect(tempId, value);
                        }}
                        disabled={!itemCategories[tempId] || (itemProducts[tempId] || []).length === 0}
                      >
                        <SelectTrigger className="w-full h-11">
                          <SelectValue placeholder={
                            !itemCategories[tempId] 
                              ? "Selecciona categoría primero" 
                              : (itemProducts[tempId] || []).length === 0 
                                ? "No hay productos activos"
                                : "Selecciona producto"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {(itemProducts[tempId] || []).length > 0 ? (
                            (itemProducts[tempId] || []).map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name} - ${product.base_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="p-2 text-sm text-gray-500 text-center">
                              No hay productos activos en esta categoría
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.items?.[index]?.product_id && (
                        <p className="text-xs text-red-600">
                          {form.formState.errors.items[index].product_id.message}
                        </p>
                      )}
                    </div>

                    {/* Cantidad / Precio / Descuento - 3 columns */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          disabled={!canEditItems}
                          className="h-11"
                          {...form.register(`items.${index}.quantity`, { 
                            valueAsNumber: true,
                            validate: (value) => {
                              if (!value || value < 1) return "Mín: 1";
                              return true;
                            }
                          })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold">Precio</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="h-11"
                          {...form.register(`items.${index}.unit_price`, { valueAsNumber: true })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold">Desc. %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          disabled={!canEditItems}
                          className="h-11"
                          {...form.register(`items.${index}.line_discount_pct`, { 
                            valueAsNumber: true
                          })}
                        />
                      </div>
                    </div>

                    {/* Total línea */}
                    <div className="flex justify-between items-center bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                      <span className="text-sm font-medium text-blue-800">Total Línea</span>
                      <span className="text-base font-bold text-blue-900">
                        ${((watchedItems[index]?.quantity || 0) * 
                           (watchedItems[index]?.unit_price || 0) * 
                           (1 - (watchedItems[index]?.line_discount_pct || 0) / 100)
                        ).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>}
                </div>
                );
              })}
            </div>
            {fields.length > 1 && (
              <div className="flex justify-end pt-2 border-t border-dashed border-gray-200">
                <Button type="button" onClick={addItem} size="sm" data-testid="add-item-bottom-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Ítem
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Totals */}
        <QuoteTotals 
          items={watchedItems || []}
          globalDiscountPct={watchedGlobalDiscount || 0}
          company={company}
        />

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/quotes')}
            >
              Cancelar
            </Button>
          </div>
          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Actualizar Cotización' : 'Crear Cotización'}
          </Button>
        </div>
      </form>

      {/* Mobile Sticky Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-3 z-30">
        <Button
          type="button"
          variant="outline"
          className="flex-1 h-11"
          onClick={() => navigate('/quotes')}
        >
          Cancelar
        </Button>
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-[2] h-11 bg-blue-600 hover:bg-blue-700 text-white"
          form="quote-form"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {isEditing ? 'Actualizar' : 'Crear'}
            </>
          )}
        </Button>
      </div>

      <ClientModal
        isOpen={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSave={onClientCreated}
      />
      
      {user?.role === 'ADMIN' && (
        <>
          <CategoryModal
            isOpen={categoryModalOpen}
            onClose={() => setCategoryModalOpen(false)}
            onSave={refreshCategories}
          />
          
          <ProductModal
            isOpen={productModalOpen}
            onClose={() => setProductModalOpen(false)}
            onSave={() => {
              // Refresh categories after creating new product
              const loadCategories = async () => {
                try {
                  const response = await apiService.getCategories({ active: true });
                  setCategories(response.items || response);
                } catch (error) {
                  console.error('Error loading categories');
                }
              };
              loadCategories();
            }}
            preselectedCategoryId={null}
          />
        </>
      )}
    </div>
  );
};

// Quote Edit Wrapper Component
const QuoteEditWrapper = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadQuote = async () => {
      try {
        const quoteData = await apiService.getQuote(id);
        setQuote(quoteData);
      } catch (error) {
        toast.error('Error al cargar la cotización');
        navigate('/quotes');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadQuote();
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <p>Cotización no encontrada</p>
      </div>
    );
  }

  // Check if quote can be edited (only BORRADOR status allows item editing)
  if (quote.status !== 'BORRADOR') {
    return (
      <div className="text-center py-12">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-lg font-medium text-orange-800 mb-2">
            Cotización No Editable
          </h3>
          <p className="text-orange-700 mb-4">
            Las cotizaciones en estado "{quote.status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : quote.status}" no pueden ser editadas.
            Solo las cotizaciones en estado "BORRADOR" permiten modificar items.
          </p>
          <Button
            onClick={() => navigate(`/quotes/${quote.id}`)}
            variant="outline"
          >
            Ver Cotización
          </Button>
        </div>
      </div>
    );
  }

  return <QuoteForm quote={quote} isEditing={true} />;
};

// Email Modal Component
const EmailModal = ({ isOpen, onClose, quote, client, onSendEmail }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    to_email: client?.email || '',
    cc_email: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    if (isOpen && client) {
      const defaultSubject = `Cotización #COT-${quote?.seq || ''} - ${client?.business_name || ''}`;
      const total = quote?.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 }) || '0.00';
      const defaultBody = `Estimado/a ${client?.contact_name || ''}, adjunto encontrará la cotización #${quote?.seq || ''} por un total de $${total} ${quote?.currency || 'MXN'}. Quedo a sus órdenes para cualquier duda. Saludos.`;
      setFormData(prev => ({
        ...prev,
        to_email: client.email || '',
        subject: defaultSubject,
        body: defaultBody
      }));
    }
  }, [isOpen, client, quote]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSendEmail(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enviar Cotización por Email</DialogTitle>
          <DialogDescription>
            Envía la cotización #{quote?.seq} con PDF adjunto
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Para (To) *</label>
            <input
              type="email"
              required
              value={formData.to_email}
              onChange={(e) => setFormData(prev => ({ ...prev, to_email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="cliente@email.com"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">CC (opcional)</label>
            <input
              type="email"
              value={formData.cc_email}
              onChange={(e) => setFormData(prev => ({ ...prev, cc_email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="copia@email.com"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Asunto (opcional)</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Usa plantilla por defecto si está vacío"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Mensaje</label>
            <textarea
              rows="4"
              value={formData.body}
              onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Usa plantilla por defecto si está vacío"
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar Email
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// WhatsApp Modal Component  
const WhatsAppModal = ({ isOpen, onClose, quote, client, onCreateLink }) => {
  const { company } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [whatsappHref, setWhatsappHref] = useState('');
  const [formData, setFormData] = useState({
    message: '',
    ttl_hours: 48
  });

  // Build default message when modal opens
  useEffect(() => {
    if (isOpen && client && quote) {
      const total = quote.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 }) || '0.00';
      setFormData(prev => ({
        ...prev,
        message: `Hola ${client.contact_name || ''}, le comparto la cotización #${quote.seq || ''} de ${company?.name || ''} por un total de $${total} ${quote?.currency || 'MXN'}. Puede revisarla aquí: {{public_link}}`
      }));
      setWhatsappHref('');
    }
  }, [isOpen, client, quote, company]);

  // Generate link immediately when modal opens, and regenerate when ttl changes
  useEffect(() => {
    if (!isOpen || !quote) return;
    let cancelled = false;
    const generate = async () => {
      setGenerating(true);
      setWhatsappHref('');
      try {
        const response = await onCreateLink(formData, true);
        if (!cancelled && response?.whatsapp_url) {
          setWhatsappHref(response.whatsapp_url);
        }
      } catch (e) { /* handled by parent */ }
      if (!cancelled) setGenerating(false);
    };
    generate();
    return () => { cancelled = true; };
  }, [isOpen, formData.ttl_hours]);

  // Regenerate when user finishes editing message (debounced)
  useEffect(() => {
    if (!isOpen || !quote || !formData.message) return;
    setWhatsappHref('');
    const timeout = setTimeout(async () => {
      setGenerating(true);
      try {
        const response = await onCreateLink(formData, true);
        if (response?.whatsapp_url) setWhatsappHref(response.whatsapp_url);
      } catch (e) { /* ignore */ }
      setGenerating(false);
    }, 600);
    return () => clearTimeout(timeout);
  }, [formData.message]);

  const displayMessage = formData.message.replace('{{public_link}}', '[link se genera automáticamente]');

  const handleLinkClick = () => {
    onCreateLink(formData, false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Compartir por WhatsApp</DialogTitle>
          <DialogDescription>
            Crea un link temporal para compartir la cotización #{quote?.seq}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mensaje</label>
            <textarea
              rows="3"
              value={displayMessage}
              onChange={(e) => {
                const val = e.target.value.replace('[link se genera automáticamente]', '{{public_link}}');
                setFormData(prev => ({ ...prev, message: val }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Usa plantilla por defecto si está vacío"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Válido por (horas)</label>
            <select
              value={formData.ttl_hours}
              onChange={(e) => setFormData(prev => ({ ...prev, ttl_hours: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value={24}>24 horas</option>
              <option value={48}>48 horas (recomendado)</option>
              <option value={72}>72 horas</option>
              <option value={168}>1 semana</option>
            </select>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            {generating ? (
              <Button disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generando...
              </Button>
            ) : whatsappHref ? (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Abrir WhatsApp
              </a>
            ) : (
              <Button disabled>
                <MessageCircle className="h-4 w-4 mr-2" />
                Abrir WhatsApp
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Quote Detail Component
const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // PDF-related states
  const [pdfMeta, setPdfMeta] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Email and sharing states
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  // Extend validity states
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(15);
  const [extendLoading, setExtendLoading] = useState(false);

  const view = new URLSearchParams(location.search).get('view');

  const loadQuote = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const quoteData = await apiService.getQuote(id);
      setQuote(quoteData);
      
      // Get client data
      const clientData = await apiService.getClient(quoteData.client_id);
      setClient(clientData);
      
      // Get PDF metadata
      try {
        const pdfMetaData = await apiService.getQuotePDFMeta(id);
        setPdfMeta(pdfMetaData);
      } catch (pdfError) {
        console.warn('PDF meta not available:', pdfError.response?.status);
        setPdfMeta(null);
      }
      
      setError(null);
    } catch (error) {
      // Enhanced error logging for debugging
      console.error('Error loading quote:', {
        status: error.response?.status,
        payload: error.response?.data,
        message: error.message,
        quoteId: id,
        timestamp: new Date().toISOString(),
        fullError: error
      });
      
      if (error.response) {
        const status = error.response.status;
        const payload = error.response.data;
        
        if (status === 404) {
          setError({
            type: 'not_found',
            title: 'Cotización no encontrada',
            message: 'La cotización solicitada no existe o no tienes permisos para verla.',
            canRetry: true
          });
        } else if (status === 400) {
          setError({
            type: 'invalid_id',
            title: 'ID inválido',
            message: 'El identificador de cotización no es válido.',
            canRetry: true
          });
        } else if (status >= 500) {
          setError({
            type: 'server_error',
            title: 'Error del servidor',
            message: 'Hubo un problema al cargar la cotización. Intenta nuevamente.',
            canRetry: true
          });
        } else if (status === 401) {
          setError({
            type: 'auth_error',
            title: 'No autenticado',
            message: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
            canRetry: false
          });
        } else {
          setError({
            type: 'unknown',
            title: 'Error inesperado',
            message: payload?.message || payload?.detail || 'No se pudo cargar la cotización.',
            canRetry: true
          });
        }
      } else {
        // Network or other error
        setError({
          type: 'network',
          title: 'Error de conexión',
          message: 'No se pudo conectar con el servidor. Verifica tu conexión.',
          canRetry: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadQuote();
    }
  }, [id]);
  
  // REMOVED: handleViewPDF - now using only handleDownloadPDF for simplicity
  
  const handleDownloadPDF = async () => {
    console.log('🔥 PDF Download button clicked!');
    
    // Singleton protection: prevent multiple concurrent PDF operations
    if (pdfLoading) {
      console.warn('PDF operation already in progress, ignoring duplicate request');
      return;
    }
    
    setPdfLoading(true);
    console.log('Starting PDF download operation...');
    
    try {
      // Check if PDF exists, if not generate it first
      if (!pdfMeta?.exists) {
        console.log('PDF does not exist, generating...');
        toast.info('Generando PDF...');
        await apiService.generateQuotePDF(id);
        
        // Refresh PDF metadata
        const updatedMeta = await apiService.getQuotePDFMeta(id);
        setPdfMeta(updatedMeta);
        console.log('PDF generated, updated metadata:', updatedMeta);
      }
      
      console.log('Downloading PDF blob...');
      // Download PDF as blob with authentication
      const blob = await apiService.downloadQuotePDF(id);
      console.log('PDF blob downloaded, size:', blob.size, 'bytes, type:', blob.type);
      
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      console.log('Created blob URL for download:', blobUrl.substring(0, 50) + '...');
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Cotizacion-COT-${quote?.seq || 'N-A'}.pdf`;
      
      console.log('Triggering download with filename:', link.download);
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        console.log('Blob URL cleaned up');
      }, 1000);
      
      toast.success('PDF descargado exitosamente', {
        description: 'El archivo se encuentra en tu carpeta de descargas'
      });
      
      console.log('✅ PDF download completed successfully');
      
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      const status = error.response?.status;
      const payload = error.response?.data;
      
      let errorMessage = 'Error al descargar PDF';
      
      if (status === 400 && payload?.detail?.message === 'quote_missing_client') {
        errorMessage = 'No se puede descargar PDF: La cotización no tiene cliente asignado';
      } else if (status === 400 && payload?.detail?.message === 'quote_missing_items') {
        errorMessage = 'No se puede descargar PDF: La cotización no tiene productos';
      } else if (payload?.detail?.message) {
        errorMessage = `Error: ${payload.detail.message}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        action: {
          label: 'Reintentar',
          onClick: () => handleDownloadPDF()
        }
      });
    } finally {
      setPdfLoading(false);
      console.log('PDF download operation finished');
    }
  };
  const handleExtendValidity = async () => {
    if (!extendDays || extendDays < 1 || extendDays > 365) {
      toast.error('Días de extensión deben estar entre 1 y 365');
      return;
    }

    setExtendLoading(true);
    try {
      const result = await apiService.extendQuoteValidity(id, extendDays);
      
      // Reload quote data to reflect changes
      await loadQuote();
      
      toast.success(result.message || 'Vigencia extendida exitosamente', {
        description: `Nueva fecha de vencimiento: ${new Date(result.new_due_date).toLocaleDateString('es-MX')}`
      });
      
      setExtendModalOpen(false);
      setExtendDays(15); // Reset to default
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al extender vigencia';
      toast.error(errorMessage);
    } finally {
      setExtendLoading(false);
    }
  };

  // REMOVED: Auto-handling of view=pdf parameter to prevent infinite loop
  // PDF now only opens when user explicitly clicks "Ver PDF" button
  
  // REMOVED: Blob URL cleanup and keyboard listeners - no longer needed with new tab approach

  const handleStatusChange = async (newStatus) => {
    try {
      await apiService.updateQuoteStatus(id, newStatus);
      setQuote(prev => ({ ...prev, status: newStatus }));
      toast.success('Estado actualizado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleDuplicate = async () => {
    try {
      const newQuote = await apiService.duplicateQuote(id);
      toast.success('Cotización duplicada exitosamente');
      navigate(`/quotes/${newQuote.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar cotización');
    }
  };

  const handleSendEmail = async (emailData) => {
    try {
      const response = await apiService.sendQuoteEmail(id, emailData);
      toast.success('Email enviado exitosamente');
      setEmailModalOpen(false);
      // Refresh quote to update tracking data
      await loadQuote();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email');
    }
  };

  const handleCreateWhatsAppLink = async (whatsAppData, preGenerate = false) => {
    try {
      const response = await apiService.createWhatsAppLink(id, whatsAppData);
      if (preGenerate) return response;
      // Post-action side effects
      toast.success('Link de WhatsApp creado exitosamente');
      await loadQuote();
      return response;
    } catch (error) {
      if (!preGenerate) toast.error(error.response?.data?.detail || 'Error al crear link de WhatsApp');
      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'BORRADOR': return 'bg-gray-100 text-gray-800';
      case 'ENVIADA': return 'bg-blue-100 text-blue-800';
      case 'NEGOCIACION': return 'bg-yellow-100 text-yellow-800';
      case 'ACEPTADA': return 'bg-green-100 text-green-800';
      case 'RECHAZADA': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{error.title}</h1>
            <Breadcrumb items={['Cotizaciones', 'Error']} />
          </div>
          {error.canRetry && (
            <Button 
              variant="outline" 
              onClick={() => loadQuote()}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <AlertCircle className="h-4 w-4 mr-2" />
              )}
              Reintentar
            </Button>
          )}
        </div>
        
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {error.title}
            </h3>
            <p className="text-gray-500 mb-4">
              {error.message}
            </p>
            <div className="flex justify-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/quotes')}
              >
                Volver a Cotizaciones
              </Button>
              {error.canRetry && (
                <Button 
                  onClick={() => loadQuote()}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-2" />
                  )}
                  Reintentar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <p>Cotización no encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost" 
            onClick={() => navigate('/quotes')}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Cotización #{quote.seq}
            </h1>
            <Breadcrumb items={['Cotizaciones', `#${quote.seq}`]} />
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={handleDuplicate}
          >
            <Copy className="h-4 w-4 mr-2" />
            Duplicar
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/quotes/${id}/edit`)}
            disabled={quote.status === 'ACEPTADA'}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          
          {/* PDF Download - Single Button */}
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={pdfLoading || !pdfMeta?.can_generate}
              title={
                !pdfMeta?.can_generate 
                  ? "No se puede generar PDF: Faltan datos del cliente o productos"
                  : pdfMeta?.exists 
                    ? "Descargar archivo PDF"
                    : "Generar y descargar PDF"
              }
            >
              {pdfLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Descargar PDF
            </Button>
            
            {/* Email Send Button */}
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(true)}
              disabled={pdfLoading || !pdfMeta?.can_generate}
              title="Enviar cotización por email"
            >
              <Mail className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
            
            {/* WhatsApp Share Button */}
            <Button
              variant="outline"
              onClick={() => setWhatsAppModalOpen(true)}
              disabled={pdfLoading || !pdfMeta?.can_generate}
              title="Compartir por WhatsApp"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden space-y-4 p-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost" 
            onClick={() => navigate('/quotes')}
            className="p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">
              Cotización #{quote.seq}
            </h1>
            <p className="text-sm text-gray-500">Ver detalles</p>
          </div>
        </div>

        {/* Mobile Action Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Row 1 */}
          <Button
            variant="outline"
            onClick={() => navigate(`/quotes/${id}/edit`)}
            disabled={quote.status === 'ACEPTADA'}
            className="h-14 flex flex-col items-center justify-center"
          >
            <Edit className="h-5 w-5 mb-1" />
            <span className="text-xs">Editar</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handleDuplicate}
            className="h-14 flex flex-col items-center justify-center"
          >
            <Copy className="h-5 w-5 mb-1" />
            <span className="text-xs">Duplicar</span>
          </Button>
          
          {/* Row 2 */}
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={pdfLoading || !pdfMeta?.can_generate}
            className="h-14 flex flex-col items-center justify-center"
          >
            {pdfLoading ? (
              <Loader2 className="h-5 w-5 mb-1 animate-spin" />
            ) : (
              <Download className="h-5 w-5 mb-1" />
            )}
            <span className="text-xs">PDF</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setEmailModalOpen(true)}
            disabled={pdfLoading || !pdfMeta?.can_generate}
            className="h-14 flex flex-col items-center justify-center"
          >
            <Mail className="h-5 w-5 mb-1" />
            <span className="text-xs">Email</span>
          </Button>
        </div>

        {/* WhatsApp Button - Full Width */}
        <Button
          variant="outline"
          onClick={() => setWhatsAppModalOpen(true)}
          disabled={pdfLoading || !pdfMeta?.can_generate}
          className="w-full h-12 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          Compartir por WhatsApp
        </Button>
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        quote={quote}
        client={client}
        onSendEmail={handleSendEmail}
      />
      
      {/* WhatsApp Modal */}
      <WhatsAppModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        quote={quote}
        client={client}
        onCreateLink={handleCreateWhatsAppLink}
      />
      
      {/* Extend Validity Modal */}
      {extendModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Extender Vigencia</h3>
                <button
                  onClick={() => setExtendModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center text-blue-700 mb-2">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span className="font-medium">Información Actual</span>
                </div>
                <p className="text-sm text-blue-600">
                  Fecha actual de vencimiento: {quote?.due_date ? new Date(quote.due_date).toLocaleDateString('es-MX') : 'No establecida'}
                </p>
                <p className="text-sm text-blue-600">
                  Días de validez actuales: {quote?.validity_days || 15} días
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Días adicionales a extender:
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: 15"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nueva fecha de vencimiento: {quote?.due_date ? new Date(new Date(quote.due_date).getTime() + extendDays * 24 * 60 * 60 * 1000).toLocaleDateString('es-MX') : 'Calculando...'}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setExtendModalOpen(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleExtendValidity}
                  disabled={extendLoading || !extendDays || extendDays < 1 || extendDays > 365}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-md transition-colors flex items-center"
                >
                  {extendLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Extender Vigencia
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Información General</CardTitle>
            <div className="flex flex-col items-end space-y-2">
              <Badge className={getStatusColor(quote.status)}>
                {quote.status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : quote.status}
              </Badge>
              {/* Acceptance Info */}
              {quote.status === 'ACEPTADA' && quote.accepted_at && (
                <div className="text-sm text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200">
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-3 w-3" />
                    <span className="font-medium">Aceptada por:</span>
                    <span>{quote.approver_name}</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {quote.approver_email} • {new Date(quote.accepted_at).toLocaleString('es-MX')}
                  </div>
                </div>
              )}
              {/* Rejection Info */}
              {quote.status === 'RECHAZADA' && quote.rejected_at && (
                <div className="text-sm text-red-700 bg-red-50 px-2 py-1 rounded border border-red-200">
                  <div className="flex items-center space-x-1">
                    <XCircle className="h-3 w-3" />
                    <span className="font-medium">Rechazada</span>
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    {new Date(quote.rejected_at).toLocaleString('es-MX')}
                    {quote.rejection_reason && (
                      <div className="mt-1 text-red-700">Motivo: {quote.rejection_reason}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Cliente</h4>
              <div className="text-gray-600">
                <p className="font-medium">{client?.business_name}</p>
                <p>{client?.contact_name}</p>
                <p>{client?.email}</p>
                {client?.phone && <p>{client.phone}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Fecha:</span>
                <span>{new Date(quote.date).toLocaleDateString('es-MX')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Validez:</span>
                <span>{quote.validity_days} días</span>
              </div>
              {quote.due_date && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vence el:</span>
                    <span className={`font-medium ${
                      new Date(quote.due_date) < new Date() 
                        ? 'text-red-600' 
                        : new Date(quote.due_date) < new Date(Date.now() + 48 * 60 * 60 * 1000)
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}>
                      {new Date(quote.due_date).toLocaleDateString('es-MX')}
                      {new Date(quote.due_date) < new Date() && ' (Vencida)'}
                      {new Date(quote.due_date) >= new Date() && 
                       new Date(quote.due_date) < new Date(Date.now() + 48 * 60 * 60 * 1000) && 
                       ' (Vence pronto)'}
                    </span>
                  </div>
                  {/* Extend Validity Button for expired or expiring quotes */}
                  {(new Date(quote.due_date) < new Date() || 
                    new Date(quote.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) && (
                    <div className="pt-2">
                      <button
                        onClick={() => setExtendModalOpen(true)}
                        className="w-full text-sm bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded-md transition-colors flex items-center justify-center"
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Extender vigencia
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Moneda:</span>
                <span>{quote.currency}</span>
              </div>
              {quote.global_discount_pct > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Descuento Global:</span>
                  <span>{quote.global_discount_pct}%</span>
                </div>
              )}
              {pdfMeta?.generated_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Última generación PDF:</span>
                  <span className="text-sm">
                    {new Date(pdfMeta.generated_at).toLocaleDateString('es-MX')} {' '}
                    {new Date(pdfMeta.generated_at).toLocaleTimeString('es-MX', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
          {quote.sales_notes && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Notas de Venta</h4>
              <p className="text-gray-600">{quote.sales_notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quote Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Historial y Seguimiento</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Creation */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">Cotización Creada</p>
                <p className="text-xs text-gray-500">
                  {new Date(quote.created_at).toLocaleString('es-MX')}
                </p>
              </div>
            </div>

            {/* Email Sent */}
            {quote.email_sent_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Email Enviado</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.email_sent_at).toLocaleString('es-MX')} → {quote.email_sent_to}
                    {quote.email_sent_cc && ` (CC: ${quote.email_sent_cc})`}
                  </p>
                </div>
              </div>
            )}

            {/* WhatsApp Link Created */}
            {quote.wa_link_created_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Link WhatsApp Creado</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.wa_link_created_at).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            )}

            {/* First Opened */}
            {quote.first_opened_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Primera Apertura</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.first_opened_at).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            )}

            {/* View Count */}
            {quote.opened_count > 0 && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Visto {quote.opened_count} {quote.opened_count === 1 ? 'vez' : 'veces'}
                  </p>
                  {quote.last_opened_at && (
                    <p className="text-xs text-gray-500">
                      Última vez: {new Date(quote.last_opened_at).toLocaleString('es-MX')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* 48h Reminder Sent */}
            {quote.reminder_48h_sent_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-yellow-800">🔔 Recordatorio Enviado (48h)</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.reminder_48h_sent_at).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            )}

            {/* Final Reminder Sent */}
            {quote.reminder_final_sent_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-orange-800">🚨 Último Aviso Enviado</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.reminder_final_sent_at).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            )}

            {/* Accepted */}
            {quote.accepted_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-green-800">✅ Cotización Aceptada</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.accepted_at).toLocaleString('es-MX')} por {quote.approver_name}
                  </p>
                  <p className="text-xs text-gray-400">{quote.approver_email}</p>
                </div>
              </div>
            )}

            {/* Rejected */}
            {quote.rejected_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-red-800">❌ Cotización Rechazada</p>
                  <p className="text-xs text-gray-500">
                    {new Date(quote.rejected_at).toLocaleString('es-MX')}
                  </p>
                  {quote.rejection_reason && (
                    <p className="text-xs text-red-600 mt-1">
                      Motivo: {quote.rejection_reason}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* PDF Generated */}
            {pdfMeta?.generated_at && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">PDF Generado</p>
                  <p className="text-xs text-gray-500">
                    {new Date(pdfMeta.generated_at).toLocaleString('es-MX')} 
                    {pdfMeta.file_size && ` • ${(pdfMeta.file_size / 1024).toFixed(1)} KB`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quote Items */}
      <Card>
        <CardHeader>
          <CardTitle>Ítems</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Mobile items cards */}
          <div className="md:hidden space-y-2">
            {quote.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-900">{item.snapshot.product_name}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Cant: {item.quantity} · ${item.unit_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  {item.line_discount_pct > 0 ? ` · -${item.line_discount_pct}%` : ''}
                  {' · '}Total: ${item.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </div>
          {/* Desktop items table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Unit.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descuento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {quote.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.snapshot.product_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.snapshot.category_name} • {item.snapshot.unit}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${item.unit_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.line_discount_pct > 0 ? `${item.line_discount_pct}%` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${item.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quote Totals */}
      <Card>
        <CardHeader>
          <CardTitle>Totales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${quote.subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
            </div>
            {quote.global_discount_pct > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Descuento Global ({quote.global_discount_pct}%):</span>
                <span>-${(quote.subtotal * quote.global_discount_pct / 100).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {quote.iva_amount > 0 && (
              <div className="flex justify-between">
                <span>IVA ({quote.iva_pct}%):</span>
                <span>${quote.iva_amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span className="text-blue-600">
                  ${quote.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {quote.currency}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Acciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2 flex-wrap">
            {['BORRADOR', 'ENVIADA', 'NEGOCIACION', 'ACEPTADA', 'RECHAZADA'].map((status) => (
              <Button
                key={status}
                variant={quote.status === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange(status)}
                disabled={quote.status === status}
              >
                {status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : status}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* PDF Viewer removed - now opens in new tab or downloads directly */}
    </div>
  );
};

// Quotes List Component
const QuotesList = () => {
  const navigate = useNavigate();
  const handleSafeNavigate = safeNavigate(navigate);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const quotesResponse = await apiService.getQuotes({
          search: searchTerm || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter
        });
        setQuotes(quotesResponse.items || quotesResponse);
      } catch (error) {
        toast.error('Error al cargar cotizaciones');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchTerm, statusFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'BORRADOR': return 'bg-gray-100 text-gray-800';
      case 'ENVIADA': return 'bg-blue-100 text-blue-800';
      case 'NEGOCIACION': return 'bg-yellow-100 text-yellow-800';
      case 'ACEPTADA': return 'bg-green-100 text-green-800';
      case 'RECHAZADA': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cotizaciones</h1>
          <Breadcrumb items={['Cotizaciones']} />
        </div>
        <Button onClick={() => navigate('/quotes/new')} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Cotización
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          {/* Desktop Filters */}
          <div className="hidden md:flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar cotizaciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="BORRADOR">Borrador</SelectItem>
                <SelectItem value="ENVIADA">Enviada</SelectItem>
                <SelectItem value="NEGOCIACION">Negociación</SelectItem>
                <SelectItem value="ACEPTADA">Aceptada</SelectItem>
                <SelectItem value="RECHAZADA">Rechazada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mobile Filters */}
          <div className="md:hidden space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Buscar cotizaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 mobile-text-base"
              />
            </div>
            
            {/* Status Filter Chips */}
            <div className="filter-chips" style={{overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '4px', display: 'flex', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch'}}>
              {[
                { value: 'all', label: 'Todos' },
                { value: 'BORRADOR', label: 'Borrador' },
                { value: 'ENVIADA', label: 'Enviada' },
                { value: 'NEGOCIACION', label: 'Negociación' },
                { value: 'ACEPTADA', label: 'Aceptada' },
                { value: 'RECHAZADA', label: 'Rechazada' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`filter-chip ${statusFilter === status.value ? 'active' : ''}`}
                  style={{flexShrink: 0}}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
              <p>Cargando cotizaciones...</p>
            </div>
          ) : quotes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p>No se encontraron cotizaciones</p>
              <Button 
                variant="outline" 
                className="mt-4" 
                onClick={() => navigate('/quotes/new')}
              >
                Crear primera cotización
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="md:hidden p-3 space-y-2">
                {quotes.map((quote) => (
                  <div key={quote.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">#COT-{quote.seq}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {quote.client_name || 'Cliente no encontrado'} · {new Date(quote.date || quote.created_at).toLocaleDateString('es-MX')}
                          </div>
                        </div>
                      </div>
                      <Badge className={`flex-shrink-0 text-xs ${getStatusColor(quote.status)}`}>
                        {quote.status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : quote.status}
                      </Badge>
                    </div>
                    <div className="mt-2 ml-12 text-sm font-medium text-gray-900">
                      ${quote.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {quote.currency}
                    </div>
                    <div className="flex justify-end mt-2 space-x-1">
                      <Button variant="ghost" size="sm" onClick={(e) => handleSafeNavigate(e, `/quotes/${quote.id}`)} className="h-8 w-8 p-0">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {quote.status !== 'ACEPTADA' && (
                        <Button variant="ghost" size="sm" onClick={(e) => handleSafeNavigate(e, `/quotes/${quote.id}/edit`)} className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Table */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            #{quote.seq}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {quote.client_name || 'Cliente no encontrado'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(quote.date || quote.created_at).toLocaleDateString('es-MX')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(quote.status)}>
                            {quote.status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : quote.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            ${quote.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {quote.currency}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              type="button"
                              aria-label="Ver cotización"
                              onClick={(e) => handleSafeNavigate(e, `/quotes/${quote.id}`)}
                              className="text-gray-400 hover:text-gray-600 p-2"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => handleSafeNavigate(e, `/quotes/${quote.id}/edit`)}
                              disabled={quote.status === 'ACEPTADA'}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Enhanced Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const handleSafeNavigate = safeNavigate(navigate);
  const [dashboardData, setDashboardData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [expiringQuotes, setExpiringQuotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotesLoading, setQuotesLoading] = useState(true);
  
  // Filters and pagination
  const [dateRange, setDateRange] = useState('30'); // days
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashboardResponse, expiringResponse] = await Promise.all([
          apiService.getDashboard(dateRange),
          apiService.getExpiringQuotes()
        ]);
        setDashboardData(dashboardResponse);
        setExpiringQuotes(expiringResponse);
      } catch (error) {
        toast.error('Error al cargar el dashboard');
        setExpiringQuotes({ critical: [], warning: [], upcoming: [], total_count: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  // SAFE MODE: Fetch quotes with filters - tolerant to failures
  const fetchQuotes = async () => {
    setQuotesLoading(true);
    try {
      const [quotesResponse, clientsResponse] = await Promise.all([
        apiService.getQuotes({
          search: searchTerm || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          date_range: dateRange,
          page: currentPage,
          limit: itemsPerPage
        }),
        apiService.getClients().catch(() => ({ items: [] })) // Fallback for clients
      ]);
      
      // SAFE MODE: Handle different response formats gracefully
      if (quotesResponse && quotesResponse.items) {
        setQuotes(quotesResponse.items || []);
        setTotalPages(quotesResponse.pages || 1);
        setTotalItems(quotesResponse.total || 0);
      } else if (Array.isArray(quotesResponse)) {
        setQuotes(quotesResponse);
        setTotalPages(1);
        setTotalItems(quotesResponse.length);
      } else if (quotesResponse && quotesResponse.error) {
        setQuotes([]);
        toast.error(quotesResponse.error || 'Error al cargar cotizaciones');
      } else {
        setQuotes([]);
        toast.error('Formato de respuesta inesperado para cotizaciones');
      }
      
      // Handle clients response safely
      if (clientsResponse && clientsResponse.items) {
        setClients(clientsResponse.items || []);
      } else if (Array.isArray(clientsResponse)) {
        setClients(clientsResponse);
      } else {
        setClients([]);
      }
      
    } catch (error) {
      console.error('Quotes fetch error:', error);
      setQuotes([]);
      setClients([]);
      setTotalPages(1);
      setTotalItems(0);
      toast.error('No se pudo cargar las cotizaciones. Reintentar en unos segundos.', {
        action: {
          label: 'Reintentar',
          onClick: () => fetchQuotes()
        }
      });
    } finally {
      setQuotesLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange]);

  useEffect(() => {
    fetchQuotes();
  }, [searchTerm, statusFilter, dateRange, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.business_name : 'Cliente no encontrado';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'BORRADOR': return <Clock className="h-4 w-4 text-gray-500" />;
      case 'ENVIADA': return <Mail className="h-4 w-4 text-blue-500" />;
      case 'NEGOCIACION': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'ACEPTADA': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'RECHAZADA': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'BORRADOR': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'ENVIADA': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'NEGOCIACION': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ACEPTADA': return 'bg-green-100 text-green-800 border-green-200';
      case 'RECHAZADA': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateKPIs = () => {
    if (!dashboardData?.quote_stats) return {
      total: 0, enviadas: 0, negociacion: 0, aceptadas: 0
    };
    
    const stats = dashboardData.quote_stats;
    return {
      total: Object.values(stats).reduce((sum, count) => sum + count, 0),
      enviadas: stats.ENVIADA || 0,
      negociacion: stats.NEGOCIACION || 0,
      aceptadas: stats.ACEPTADA || 0
    };
  };

  const kpis = calculateKPIs();

  const handleKPIClick = (status) => {
    setStatusFilter(status === 'total' ? 'all' : status.toUpperCase());
    setCurrentPage(1);
  };

  const handleStatusChipClick = (status) => {
    setStatusFilter(statusFilter === status ? 'all' : status);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona tus cotizaciones y ve el rendimiento de tu negocio
          </p>
        </div>
        <div className="flex flex-nowrap items-center gap-1.5 md:flex-wrap md:gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/clients')}
            className="text-gray-600 hover:text-gray-900 text-[0.8rem] px-2.5 h-8 whitespace-nowrap md:text-sm md:px-4 md:h-10"
          >
            <Users className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
            Clientes
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/catalog/categories')}
            className="text-gray-600 hover:text-gray-900 text-[0.8rem] px-2.5 h-8 whitespace-nowrap md:text-sm md:px-4 md:h-10"
          >
            <Package className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
            Catálogo
          </Button>
          <Button 
            onClick={() => navigate('/quotes/new')} 
            className="bg-blue-600 hover:bg-blue-700 text-[0.8rem] px-2.5 h-8 whitespace-nowrap md:text-sm md:px-4 md:h-10"
          >
            <Plus className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
            Nueva Cotización
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">Período:</span>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="all">Todo el tiempo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-3 md:grid-cols-3 md:gap-4 xl:grid-cols-5 xl:gap-5">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden"
          onClick={() => handleKPIClick('total')}
        >
          <CardContent className="p-2 md:p-6">
            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
              <div className="flex-shrink-0">
                <div className="p-1.5 md:p-3 bg-slate-100 rounded-lg">
                  <FileText className="h-4 w-4 md:h-6 md:w-6 text-slate-600" />
                </div>
              </div>
              <div className="mt-1 md:mt-0 md:ml-5 md:w-0 md:flex-1">
                <dl>
                  <dt className="text-[0.65rem] leading-tight md:text-xs lg:text-sm font-medium text-gray-500">Total</dt>
                  <dd className="text-[1.3rem] md:text-lg lg:text-2xl font-semibold text-gray-900">
                    {kpis.total}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden"
          onClick={() => handleKPIClick('enviada')}
        >
          <CardContent className="p-2 md:p-6">
            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
              <div className="flex-shrink-0">
                <div className="p-1.5 md:p-3 bg-blue-100 rounded-lg">
                  <Send className="h-4 w-4 md:h-6 md:w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-1 md:mt-0 md:ml-5 md:w-0 md:flex-1">
                <dl>
                  <dt className="text-[0.65rem] leading-tight md:text-xs lg:text-sm font-medium text-gray-500">Enviadas</dt>
                  <dd className="text-[1.3rem] md:text-lg lg:text-2xl font-semibold text-gray-900">
                    {kpis.enviadas}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden"
          onClick={() => handleKPIClick('negociacion')}
        >
          <CardContent className="p-2 md:p-6">
            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
              <div className="flex-shrink-0">
                <div className="p-1.5 md:p-3 bg-yellow-100 rounded-lg">
                  <AlertCircle className="h-4 w-4 md:h-6 md:w-6 text-yellow-600" />
                </div>
              </div>
              <div className="mt-1 md:mt-0 md:ml-5 md:w-0 md:flex-1">
                <dl>
                  <dt className="text-[0.65rem] leading-tight md:text-xs lg:text-sm font-medium text-gray-500">Negociación</dt>
                  <dd className="text-[1.3rem] md:text-lg lg:text-2xl font-semibold text-gray-900">
                    {kpis.negociacion}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden"
          onClick={() => handleKPIClick('aceptada')}
        >
          <CardContent className="p-2 md:p-6">
            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
              <div className="flex-shrink-0">
                <div className="p-1.5 md:p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-4 w-4 md:h-6 md:w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-1 md:mt-0 md:ml-5 md:w-0 md:flex-1">
                <dl>
                  <dt className="text-[0.65rem] leading-tight md:text-xs lg:text-sm font-medium text-gray-500">Aceptadas</dt>
                  <dd className="text-[1.3rem] md:text-lg lg:text-2xl font-semibold text-gray-900">
                    {kpis.aceptadas}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Por Vencer Card */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden"
          onClick={() => {
            setStatusFilter('ENVIADA');
            // TODO: Add date filter for expiring soon
          }}
        >
          <CardContent className="p-2 md:p-6">
            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
              <div className="flex-shrink-0">
                <div className={`p-1.5 md:p-3 rounded-lg ${
                  expiringQuotes?.critical?.length > 0 
                    ? 'bg-red-100'
                    : expiringQuotes?.warning?.length > 0
                      ? 'bg-yellow-100'  
                      : 'bg-orange-100'
                }`}>
                  <Clock className={`h-4 w-4 md:h-6 md:w-6 ${
                    expiringQuotes?.critical?.length > 0
                      ? 'text-red-600'
                      : expiringQuotes?.warning?.length > 0
                        ? 'text-yellow-600'
                        : 'text-orange-600'
                  }`} />
                </div>
              </div>
              <div className="mt-1 md:mt-0 md:ml-5 md:w-0 md:flex-1">
                <dl>
                  <dt className="text-[0.65rem] leading-tight md:text-xs lg:text-sm font-medium text-gray-500">Por Vencer</dt>
                  <dd className="text-[1.3rem] md:text-lg lg:text-2xl font-semibold text-gray-900">
                    {expiringQuotes?.total_count || 0}
                  </dd>
                  {expiringQuotes && (
                    <dd className="text-xs text-gray-500 mt-1">
                      {expiringQuotes.critical?.length > 0 && (
                        <span className="text-red-600">{expiringQuotes.critical.length} críticas</span>
                      )}
                      {expiringQuotes.warning?.length > 0 && (
                        <span className={expiringQuotes.critical?.length > 0 ? 'ml-2 text-yellow-600' : 'text-yellow-600'}>
                          {expiringQuotes.warning.length} próximas
                        </span>
                      )}
                    </dd>
                  )}
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {kpis.total === 0 && !loading ? (
        /* Onboarding Empty State */
        <Card data-testid="onboarding-empty-state">
          <CardContent className="py-12 px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Bienvenido a Presuply! Sigue estos pasos para empezar
              </h2>
              <p className="text-sm text-gray-500">Solo toma unos minutos configurar tu cuenta</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="border rounded-lg p-6 text-center space-y-4 hover:shadow-md transition-shadow" data-testid="onboarding-step-catalog">
                <div className="mx-auto w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Package className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Configura tu catálogo</h3>
                  <p className="text-sm text-gray-500 mt-1">Agrega categorias y productos</p>
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate('/catalog/categories')} data-testid="onboarding-btn-catalog">
                  Ir al Catálogo
                </Button>
              </div>
              <div className="border rounded-lg p-6 text-center space-y-4 hover:shadow-md transition-shadow" data-testid="onboarding-step-clients">
                <div className="mx-auto w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Agrega un cliente</h3>
                  <p className="text-sm text-gray-500 mt-1">Registra a tus clientes para cotizar</p>
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate('/clients')} data-testid="onboarding-btn-clients">
                  Ir a Clientes
                </Button>
              </div>
              <div className="border rounded-lg p-6 text-center space-y-4 hover:shadow-md transition-shadow" data-testid="onboarding-step-quote">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Crea tu primera cotización</h3>
                  <p className="text-sm text-gray-500 mt-1">Envía una cotización profesional</p>
                </div>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/quotes/new')} data-testid="onboarding-btn-quote">
                  Crear Cotización
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
        {/* Filters and Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Buscar por cliente o número de cotización..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Status Filter Pills */}
              <div className="hidden md:flex items-center space-x-2">
                <span className="text-sm text-gray-500">Filtrar:</span>
                <div className="flex flex-wrap gap-2">
                  {['BORRADOR', 'ENVIADA', 'NEGOCIACION', 'ACEPTADA', 'RECHAZADA'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChipClick(status)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        statusFilter === status
                          ? getStatusColor(status)
                          : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : status}
                    </button>
                  ))}
                  {statusFilter !== 'all' && (
                    <button
                      onClick={() => setStatusFilter('all')}
                      className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-500 border-gray-300 hover:bg-gray-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Cotizaciones Recientes</CardTitle>
                <CardDescription>
                  {totalItems > 0 ? `${totalItems} cotizaciones encontradas` : 'Historial de cotizaciones'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {quotesLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                <p>Cargando cotizaciones...</p>
              </div>
            ) : quotes.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No se encontraron cotizaciones
                </h3>
                <p className="text-gray-500 mb-4">
                  Intenta cambiar los filtros de búsqueda
                </p>
                <Button onClick={() => navigate('/quotes/new')} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Cotización
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-[0.85rem] md:text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {quotes.map((quote) => (
                        <tr key={quote.id} className="hover:bg-gray-50">
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/quotes/${quote.id}?view=pdf`}
                              onClick={(e) => handleSafeNavigate(e, `/quotes/${quote.id}?view=pdf`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              #COT-{quote.seq}
                            </Link>
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 hidden md:flex">
                                <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <Building2 className="h-4 w-4 text-gray-600" />
                                </div>
                              </div>
                              <div className="md:ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {quote.client_name || getClientName(quote.client_id)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(quote.date || quote.created_at).toLocaleDateString('es-MX')}
                            </div>
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(quote.status)}`}>
                              {quote.status === 'NEGOCIACION' ? 'NEGOCIACIÓN' : quote.status}
                            </span>
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              ${quote.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

// Other components (Categories, Products, Clients) remain the same as before for brevity
// I'll just include their definitions without repeating the full implementation

// Category Modal Component
const CategoryModal = ({ isOpen, onClose, category, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category?.name || '',
      description: category?.description || '',
      active: category?.active ?? true,
    }
  });

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name || '',
        description: category.description || '',
        active: category.active ?? true,
      });
    } else {
      form.reset({
        name: '',
        description: '',
        active: true,
      });
    }
  }, [category, form]);

  const onSubmit = async (data) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    setLoading(true);
    try {
      if (category) {
        await apiService.updateCategory(category.id, data);
        toast.success('Categoría actualizada exitosamente');
      } else {
        await apiService.createCategory(data);
        toast.success('Categoría creada exitosamente');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la categoría');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {category ? 'Editar Categoría' : 'Nueva Categoría'}
          </DialogTitle>
          <DialogDescription>
            {category 
              ? 'Actualiza los datos de la categoría'
              : 'Crea una nueva categoría para organizar tus productos'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              placeholder="Ej: Software, Hardware, Servicios"
              {...form.register('name')}
              disabled={user?.role !== 'ADMIN'}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              placeholder="Descripción opcional de la categoría"
              {...form.register('description')}
              disabled={user?.role !== 'ADMIN'}
              rows={3}
            />
            {form.formState.errors.description && (
              <p className="text-sm text-red-600">{form.formState.errors.description.message}</p>
            )}
          </div>

          {category && (
            <div className="flex items-center space-x-2">
              <Switch 
                id="active"
                {...form.register('active')}
                disabled={user?.role !== 'ADMIN'}
              />
              <Label htmlFor="active">Activa</Label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            {user?.role === 'ADMIN' && (
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {category ? 'Actualizar' : 'Crear'}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Product Modal Component
const ProductModal = ({ isOpen, onClose, product, onSave, preselectedCategoryId = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const form = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      category_id: product?.category_id || preselectedCategoryId || '',
      name: product?.name || '',
      short_description: product?.short_description || '',
      unit: product?.unit || 'pcs',
      base_price: product?.base_price || 0,
      active: product?.active ?? true,
    }
  });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await apiService.getCategories({ active: true });
        setCategories(response.items || response);
      } catch (error) {
        toast.error('Error al cargar categorías');
      }
    };
    
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (product) {
      form.reset({
        category_id: product.category_id || preselectedCategoryId || '',
        name: product.name || '',
        short_description: product.short_description || '',
        unit: product.unit || 'pcs',
        base_price: product.base_price || 0,
        active: product.active ?? true,
      });
    } else {
      form.reset({
        category_id: preselectedCategoryId || '',
        name: '',
        short_description: '',
        unit: 'pcs',
        base_price: 0,
        active: true,
      });
    }
  }, [product, form, preselectedCategoryId]);

  const onSubmit = async (data) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    setLoading(true);
    try {
      if (product) {
        await apiService.updateProduct(product.id, data);
        toast.success('Producto actualizado exitosamente');
      } else {
        await apiService.createProduct(data);
        toast.success('Producto creado exitosamente');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el producto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {product ? 'Editar Producto' : 'Nuevo Producto'}
          </DialogTitle>
          <DialogDescription>
            {product 
              ? 'Actualiza los datos del producto'
              : 'Crea un nuevo producto en tu catálogo'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category_id">Categoría *</Label>
            <Select 
              onValueChange={(value) => form.setValue('category_id', value)}
              defaultValue={form.getValues('category_id')}
              disabled={user?.role !== 'ADMIN'}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una categoría" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.category_id && (
              <p className="text-sm text-red-600">{form.formState.errors.category_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              placeholder="Ej: Laptop Dell Inspiron"
              {...form.register('name')}
              disabled={user?.role !== 'ADMIN'}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="short_description">Descripción Corta</Label>
            <Input
              id="short_description"
              placeholder="Descripción breve del producto"
              {...form.register('short_description')}
              disabled={user?.role !== 'ADMIN'}
            />
            {form.formState.errors.short_description && (
              <p className="text-sm text-red-600">{form.formState.errors.short_description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unit">Unidad *</Label>
              <Input
                id="unit"
                placeholder="pcs, kg, m, hrs"
                {...form.register('unit')}
                disabled={user?.role !== 'ADMIN'}
              />
              {form.formState.errors.unit && (
                <p className="text-sm text-red-600">{form.formState.errors.unit.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="base_price">Precio Base *</Label>
              <Input
                id="base_price"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...form.register('base_price', { valueAsNumber: true })}
                disabled={user?.role !== 'ADMIN'}
              />
              {form.formState.errors.base_price && (
                <p className="text-sm text-red-600">{form.formState.errors.base_price.message}</p>
              )}
            </div>
          </div>

          {product && (
            <div className="flex items-center space-x-2">
              <Switch 
                id="active"
                {...form.register('active')}
                disabled={user?.role !== 'ADMIN'}
              />
              <Label htmlFor="active">Activo</Label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            {user?.role === 'ADMIN' && (
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {product ? 'Actualizar' : 'Crear'}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Categories Component
const Categories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active'); // Default to 'active' per Fase 1 requirements
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const fetchCategories = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        active: activeFilter === 'all' ? 'all' : activeFilter === 'active' ? 'true' : 'false',
        page: currentPage,
        limit: itemsPerPage
      };
      const response = await apiService.getCategories(params);
      
      // SAFE MODE: Handle different response formats gracefully
      if (response && response.items) {
        // Paginated response
        setCategories(response.items || []);
        setTotalPages(response.pages || 1);
        setTotalItems(response.total || 0);
      } else if (Array.isArray(response)) {
        // Non-paginated response (fallback)
        setCategories(response);
        setTotalPages(1);
        setTotalItems(response.length);
      } else if (response && response.error) {
        // Backend returned error but still responded
        setCategories([]);
        toast.error(response.error || 'Error al cargar categorías');
      } else {
        // Unknown response format
        setCategories([]);
        toast.error('Formato de respuesta inesperado');
      }
    } catch (error) {
      console.error('Categories fetch error:', error);
      setCategories([]);
      setTotalPages(1);
      setTotalItems(0);
      toast.error('No se pudo cargar las categorías. Reintentar en unos segundos.', {
        action: {
          label: 'Reintentar',
          onClick: () => fetchCategories()
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    fetchCategories();
  }, [searchTerm, activeFilter, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleToggleActive = async (category) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    // When deactivating, check for active products
    if (category.active) {
      try {
        const productsRes = await apiService.getProducts({ category_id: category.id, active: 'true', limit: 1 });
        const activeCount = productsRes.total || productsRes.items?.length || 0;
        if (activeCount > 0) {
          const confirmed = window.confirm(
            `Esta categoría tiene ${activeCount} producto${activeCount > 1 ? 's' : ''} activo${activeCount > 1 ? 's' : ''}. Al desactivarla, sus productos también se desactivarán. ¿Continuar?`
          );
          if (!confirmed) return;
        }
      } catch (e) { /* proceed anyway */ }
    }

    try {
      await apiService.toggleCategoryActive(category.id);
      toast.success(`Categoría ${category.active ? 'desactivada' : 'activada'} exitosamente`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (category) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    if (window.confirm('¿Estás seguro de que quieres eliminar esta categoría?')) {
      try {
        await apiService.deleteCategory(category.id);
        toast.success('Categoría eliminada exitosamente');
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
      }
    }
  };

  const openModal = (category = null) => {
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCategory(null);
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Categorías</h1>
          <Breadcrumb items={['Catálogo', 'Categorías']} />
        </div>
        {user?.role === 'ADMIN' && (
          <Button onClick={() => openModal()} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Categoría
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar categorías..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="inactive">Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Categories Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
              <p>Cargando categorías...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              {searchTerm ? (
                <>
                  <p>No se encontraron categorías</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Organiza tu catálogo con categorías</h3>
                  <p className="text-gray-500 mb-4">Las categorías agrupan tus productos o servicios. Por ejemplo: 'Mobiliario', 'Servicios', 'Equipos'.</p>
                  {user?.role === 'ADMIN' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700" 
                      onClick={() => openModal()}
                    >
                      Crear primera categoría
                    </Button>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden p-3 space-y-2">
                {categories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Tag className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{category.name}</div>
                          <div className="text-xs text-gray-500 truncate">{category.description || 'Sin descripción'}</div>
                        </div>
                      </div>
                      <Badge className={`flex-shrink-0 text-xs ${category.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {category.active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    <div className="mt-2 ml-12">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">Uso: {category.usage_count || 0}</Badge>
                    </div>
                    <div className="flex justify-end mt-2 space-x-1">
                      {user?.role === 'ADMIN' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(category)} className="h-8 w-8 p-0">
                            {category.active ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openModal(category)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(category)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table view */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uso
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Tag className="h-5 w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {category.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {category.description || 'Sin descripción'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Badge 
                              variant="secondary" 
                              className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                              title="Usado en X cotizaciones (histórico)"
                            >
                              Uso: {category.usage_count || 0}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Badge className={category.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {category.active ? 'Activa' : 'Inactiva'}
                            </Badge>
                            {user?.role === 'ADMIN' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(category)}
                                className="p-1"
                              >
                                {category.active ? 
                                  <ToggleRight className="h-4 w-4 text-green-600" /> : 
                                  <ToggleLeft className="h-4 w-4 text-gray-400" />
                                }
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {user?.role === 'ADMIN' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => openModal(category)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDelete(category)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      <CategoryModal
        isOpen={modalOpen}
        onClose={closeModal}
        category={selectedCategory}
        onSave={fetchCategories}
      />
    </div>
  );
};

// Products Component
const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active'); // Default to 'active' per Fase 1 requirements
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  
  const fetchProducts = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        category_id: categoryFilter === 'all' ? undefined : categoryFilter,
        active: activeFilter === 'all' ? 'all' : activeFilter === 'active' ? 'true' : 'false',
        page: currentPage,
        limit: itemsPerPage
      };
      const response = await apiService.getProducts(params);
      
      // SAFE MODE: Handle different response formats gracefully
      if (response && response.items) {
        // Paginated response
        setProducts(response.items || []);
        setTotalPages(response.pages || 1);
        setTotalItems(response.total || 0);
      } else if (Array.isArray(response)) {
        // Non-paginated response (fallback)
        setProducts(response);
        setTotalPages(1);
        setTotalItems(response.length);
      } else if (response && response.error) {
        // Backend returned error but still responded
        setProducts([]);
        toast.error(response.error || 'Error al cargar productos');
      } else {
        // Unknown response format
        setProducts([]);
        toast.error('Formato de respuesta inesperado');
      }
    } catch (error) {
      console.error('Products fetch error:', error);
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
      toast.error('No se pudo cargar los productos. Reintentar en unos segundos.', {
        action: {
          label: 'Reintentar',
          onClick: () => fetchProducts()
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories({ active: true });
      
      // SAFE MODE: Handle different response formats gracefully  
      if (response && response.items) {
        setCategories(response.items || []);
      } else if (Array.isArray(response)) {
        setCategories(response);
      } else if (response && response.error) {
        setCategories([]);
        toast.error('Error al cargar categorías para filtros');
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Categories filter fetch error:', error);
      setCategories([]);
      toast.error('Error al cargar categorías para filtros');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, categoryFilter, activeFilter]);

  useEffect(() => {
    fetchProducts();
  }, [searchTerm, categoryFilter, activeFilter, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Sin categoría';
  };

  const handleToggleActive = async (product) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    try {
      await apiService.toggleProductActive(product.id);
      toast.success(`Producto ${product.active ? 'desactivado' : 'activado'} exitosamente`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (product) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      setDeleteLoading(true);
      setDeleteError(null);
      
      try {
        await apiService.deleteProduct(product.id);
        
        // Update products list optimistically or refetch
        setProducts(prev => prev.filter(p => p.id !== product.id));
        toast.success('Producto eliminado exitosamente');
        
      } catch (error) {
        const status = error.response?.status;
        const payload = error.response?.data;
        
        if (status === 409 && payload?.detail?.blocked) {
          // Show modal for blocked deletion
          setDeleteProduct(product);
          setDeleteError({
            blocked: true,
            reason: payload.detail.reason,
            count: payload.detail.count,
            message: payload.detail.message
          });
          setDeleteModalOpen(true);
        } else {
          // Show toast for other errors
          const errorMessage = payload?.message || payload?.detail || error.message || 'Error al eliminar producto';
          toast.error(errorMessage);
        }
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleInactivateProduct = async () => {
    if (!deleteProduct) return;
    
    setDeleteLoading(true);
    try {
      await apiService.toggleProductActive(deleteProduct.id);
      
      // Update the product in the list to show inactive status
      setProducts(prev => prev.map(p => 
        p.id === deleteProduct.id 
          ? { ...p, active: false }
          : p
      ));
      
      setDeleteModalOpen(false);
      setDeleteProduct(null);
      setDeleteError(null);
      
      toast.success('Producto inactivado. Ya no aparece en el cotizador');
      
    } catch (error) {
      console.error('Inactivate product error:', error);
      toast.error(error.response?.data?.detail || 'Error al inactivar producto');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openModal = (product = null) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Productos</h1>
          <Breadcrumb items={['Catálogo', 'Productos']} />
        </div>
        {user?.role === 'ADMIN' && (
          <Button onClick={() => openModal()} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Producto
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
              <p>Cargando productos...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              {searchTerm ? (
                <>
                  <p>No se encontraron productos</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Agrega tus productos o servicios</h3>
                  <p className="text-gray-500 mb-4">Los productos son los artículos o servicios que incluyes en tus cotizaciones. Por ejemplo: 'Silla ejecutiva', 'Instalación', 'Consultoría'.</p>
                  {user?.role === 'ADMIN' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700" 
                      onClick={() => openModal()}
                    >
                      Crear primer producto
                    </Button>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden p-3 space-y-2">
                {products.map((product) => (
                  <div key={product.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-green-100 flex items-center justify-center">
                          <Package className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{product.name}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {getCategoryName(product.category_id)} · ${product.base_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                      <Badge className={`flex-shrink-0 text-xs ${product.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {product.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="flex justify-end mt-2 space-x-1">
                      {user?.role === 'ADMIN' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(product)} className="h-8 w-8 p-0">
                            {product.active ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openModal(product)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(product)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table view */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Precio Base
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uso
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                                <Package className="h-5 w-5 text-green-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {product.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {product.short_description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {getCategoryName(product.category_id)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            ${product.base_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </div>
                          <div className="text-sm text-gray-500">
                            por {product.unit}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Badge 
                              variant="secondary" 
                              className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                              title="Usado en X cotizaciones (histórico)"
                            >
                              Uso: {product.usage_count || 0}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Badge className={product.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {product.active ? 'Activo' : 'Inactivo'}
                            </Badge>
                            {user?.role === 'ADMIN' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(product)}
                                className="p-1"
                              >
                                {product.active ? 
                                  <ToggleRight className="h-4 w-4 text-green-600" /> : 
                                  <ToggleLeft className="h-4 w-4 text-gray-400" />
                                }
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {user?.role === 'ADMIN' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => openModal(product)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDelete(product)}
                                  disabled={deleteLoading}
                                  className="text-red-600 hover:text-red-700 disabled:opacity-50"
                                >
                                  {deleteLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ProductModal
        isOpen={modalOpen}
        onClose={closeModal}
        product={selectedProduct}
        onSave={fetchProducts}
      />

      {/* Delete Blocked Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <span>No se puede eliminar</span>
            </DialogTitle>
            <DialogDescription>
              El producto no se puede eliminar porque está siendo utilizado.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {deleteError && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-orange-800">
                      Producto en uso
                    </h4>
                    <p className="text-sm text-orange-700 mt-1">
                      {deleteError.message}
                    </p>
                    {deleteError.count && (
                      <p className="text-sm text-orange-600 mt-2">
                        Aparece en <strong>{deleteError.count}</strong> cotización(es).
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ToggleLeft className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">
                    Alternativa: Inactivar producto
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Al inactivar el producto, ya no aparecerá en el cotizador pero se mantendrá el historial de cotizaciones.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeleteProduct(null);
                setDeleteError(null);
              }}
              disabled={deleteLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleInactivateProduct}
              disabled={deleteLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {deleteLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ToggleLeft className="h-4 w-4 mr-2" />
              )}
              Inactivar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Clients Component (Enhanced)
const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active'); // Default to 'active' per Fase 1 requirements
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const fetchClients = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        active: activeFilter === 'all' ? undefined : activeFilter === 'active',
        page: currentPage,
        limit: itemsPerPage
      };
      const response = await apiService.getClients(params);
      
      if (response.items) {
        // Paginated response
        setClients(response.items);
        setTotalPages(response.pages || 1);
        setTotalItems(response.total || 0);
      } else {
        // Non-paginated response (fallback)
        setClients(response);
        setTotalPages(1);
        setTotalItems(response.length);
      }
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    fetchClients();
  }, [searchTerm, activeFilter, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleToggleActive = async (client) => {
    try {
      await apiService.toggleClientActive(client.id);
      toast.success(`Cliente ${client.active ? 'desactivado' : 'activado'} exitosamente`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (client) => {
    if (user?.role !== 'ADMIN') {
      toast.error('No tienes permisos para realizar esta acción');
      return;
    }

    if (window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      try {
        await apiService.deleteClient(client.id);
        toast.success('Cliente eliminado exitosamente');
        fetchClients();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar cliente');
      }
    }
  };

  const openModal = (client = null) => {
    setSelectedClient(client);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedClient(null);
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Clientes</h1>
          <Breadcrumb items={['Clientes']} />
        </div>
        <Button onClick={() => openModal()} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
              <p>Cargando clientes...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              {searchTerm ? (
                <>
                  <p>No se encontraron clientes</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Agrega tus primeros clientes</h3>
                  <p className="text-gray-500 mb-4">Los clientes son las empresas o personas a quienes envías cotizaciones. Puedes agregar todos los datos de contacto y RFC.</p>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700" 
                    onClick={() => openModal()}
                  >
                    Crear primer cliente
                  </Button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden p-3 space-y-2">
                {clients.map((client) => (
                  <div key={client.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{client.business_name}</div>
                          <div className="text-xs text-gray-500 truncate">{client.contact_name}</div>
                        </div>
                      </div>
                      <Badge className={`flex-shrink-0 text-xs ${client.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {client.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="mt-2 ml-12 space-y-0.5 text-xs text-gray-500">
                      {client.email && <div className="truncate">{client.email}</div>}
                      {client.phone && <div>{client.phone}</div>}
                    </div>
                    <div className="flex justify-end mt-2 space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openModal(client)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user?.role === 'ADMIN' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(client)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table view */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contacto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {client.business_name}
                              </div>
                              {client.tax_id && (
                                <div className="text-sm text-gray-500">
                                  RFC: {client.tax_id}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm text-gray-900">{client.contact_name}</div>
                            <div className="text-sm text-gray-500">{client.email}</div>
                            {client.phone && (
                              <div className="text-sm text-gray-500">{client.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Badge className={client.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {client.active ? 'Activo' : 'Inactivo'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(client)}
                              className="p-1"
                            >
                              {client.active ? 
                                <ToggleRight className="h-4 w-4 text-green-600" /> : 
                                <ToggleLeft className="h-4 w-4 text-gray-400" />
                              }
                            </Button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openModal(client)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user?.role === 'ADMIN' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(client)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ClientModal
        isOpen={modalOpen}
        onClose={closeModal}
        client={selectedClient}
        onSave={fetchClients}
      />
    </div>
  );
};


// Preferences Tab Component
const PreferenciasTab = () => {
  const { company, refreshCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ivaEnabled, setIvaEnabled] = useState(true);
  const [ivaPct, setIvaPct] = useState(16);
  const [currency, setCurrency] = useState('MXN');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.axiosInstance.get('/company/preferences');
        setIvaEnabled(res.data.iva_enabled);
        setIvaPct(res.data.iva_pct);
        setCurrency(res.data.currency);
      } catch (e) {
        if (company) {
          setIvaEnabled(company.iva_enabled ?? true);
          setIvaPct(company.iva_pct ?? 16);
          setCurrency(company.currency ?? 'MXN');
        }
      }
    };
    load();
  }, [company]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.axiosInstance.put('/company/preferences', {
        iva_enabled: ivaEnabled,
        iva_pct: ivaPct,
        currency,
      });
      await refreshCompany();
      toast.success('Preferencias actualizadas');
    } catch (err) {
      toast.error('Error al guardar preferencias');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="settings-preferencias">
      <CardHeader>
        <CardTitle>Preferencias de Cotización</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-6 max-w-md">
          <div className="space-y-3">
            <Label className="text-base font-medium">IVA (Impuesto al Valor Agregado)</Label>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">{ivaEnabled ? 'Activado' : 'Desactivado'}</span>
              <button
                type="button"
                onClick={() => setIvaEnabled(!ivaEnabled)}
                data-testid="iva-toggle"
              >
                {ivaEnabled
                  ? <ToggleRight className="h-8 w-8 text-blue-600" />
                  : <ToggleLeft className="h-8 w-8 text-gray-400" />}
              </button>
            </div>
            {ivaEnabled && (
              <div className="space-y-1">
                <Label htmlFor="iva-pct">Porcentaje de IVA (%)</Label>
                <Input
                  id="iva-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={ivaPct}
                  onChange={(e) => setIvaPct(parseFloat(e.target.value) || 0)}
                  data-testid="iva-pct-input"
                />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="currency">Moneda</Label>
            <select
              id="currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="currency-select"
            >
              <option value="MXN">MXN — Peso Mexicano</option>
              <option value="USD">USD — Dólar Estadounidense</option>
            </select>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="prefs-save-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar cambios
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Settings Page Component
const SettingsPage = () => {
  const { user, company } = useAuth();
  const [activeTab, setActiveTab] = useState('empresa');
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await apiService.axiosInstance.get('/company/profile');
        const d = res.data;
        setFormData({
          name: d.name || '',
          address: d.address || '',
          phone: d.phone || '',
          email: d.email || '',
          website: d.website || '',
          instagram: d.instagram || '',
          facebook: d.facebook || '',
        });
        if (d.logo_url) {
          const logoSrc = d.logo_url.startsWith('http') ? d.logo_url : `${apiService.axiosInstance.defaults.baseURL}${d.logo_url.startsWith('/') ? d.logo_url : '/' + d.logo_url}`;
          setLogoPreview(logoSrc);
        }
        console.log('[LOGO DEBUG] logo_url from API:', d.logo_url);
      } catch (err) {
        // fallback to context data
        if (company) {
          setFormData({
            name: company.name || '',
            address: company.branding?.address || '',
            phone: company.branding?.phone || '',
            email: company.branding?.email || '',
            website: company.branding?.website || '',
            instagram: company.branding?.instagram || '',
            facebook: company.branding?.facebook || '',
          });
          if (company.logo_url) {
            const logoSrc2 = company.logo_url.startsWith('http') ? company.logo_url : `${apiService.axiosInstance.defaults.baseURL}${company.logo_url.startsWith('/') ? company.logo_url : '/' + company.logo_url}`;
            setLogoPreview(logoSrc2);
          }
        }
      }
    };
    loadProfile();
  }, [company]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        await apiService.axiosInstance.post('/company/logo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setLogoFile(null);
      }
      await apiService.axiosInstance.put('/company/profile', formData);
      toast.success('Cambios guardados correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'empresa', label: 'Mi Empresa', icon: Building2 },
    ...(user?.role === 'ADMIN' ? [{ id: 'usuarios', label: 'Usuarios', icon: Users }] : []),
    { id: 'preferencias', label: 'Preferencias', icon: Settings },
    { id: 'plan', label: 'Plan', icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Ajustes</h1>
        <p className="text-sm text-gray-500 mt-1">Configura tu cuenta y empresa</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Tab Navigation */}
        <div className="md:w-56 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`settings-tab-${tab.id}`}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'empresa' && (
            <Card data-testid="settings-empresa">
              <CardHeader>
                <CardTitle>Mi Empresa</CardTitle>
                <CardDescription>Información de tu empresa que aparece en cotizaciones y PDFs</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSave} className="space-y-6">
                  {/* Logo */}
                  <div className="space-y-2">
                    <Label>Logo</Label>
                    <div className="flex items-center space-x-4">
                      <div className="h-20 w-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                        ) : (
                          <Upload className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <label className="cursor-pointer">
                          <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                            <Upload className="h-4 w-4 mr-2" />
                            Subir imagen
                          </span>
                          <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                        </label>
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG. Max 2MB.</p>
                      </div>
                    </div>
                  </div>

                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nombre de empresa *</Label>
                    <Input
                      id="company_name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="settings-company-name"
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Label htmlFor="company_address">Dirección</Label>
                    <Input
                      id="company_address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Av. Reforma 123, CDMX"
                      data-testid="settings-company-address"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="company_phone">Teléfono</Label>
                      <Input
                        id="company_phone"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+52 55 1234 5678"
                        data-testid="settings-company-phone"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="company_email">Email de contacto</Label>
                      <Input
                        id="company_email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="contacto@empresa.com"
                        data-testid="settings-company-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Website */}
                    <div className="space-y-2">
                      <Label htmlFor="company_website">Sitio web</Label>
                      <Input
                        id="company_website"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://www.empresa.com"
                        data-testid="settings-company-website"
                      />
                    </div>

                    {/* Instagram */}
                    <div className="space-y-2">
                      <Label htmlFor="company_instagram">Instagram</Label>
                      <Input
                        id="company_instagram"
                        value={formData.instagram}
                        onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                        placeholder="@miempresa"
                        data-testid="settings-company-instagram"
                      />
                    </div>
                  </div>

                  {/* Facebook */}
                  <div className="space-y-2">
                    <Label htmlFor="company_facebook">Facebook</Label>
                    <Input
                      id="company_facebook"
                      value={formData.facebook}
                      onChange={(e) => setFormData(prev => ({ ...prev, facebook: e.target.value }))}
                      placeholder="facebook.com/miempresa"
                      data-testid="settings-company-facebook"
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-btn">
                      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Guardar cambios
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {activeTab === 'usuarios' && (
            <Card data-testid="settings-usuarios">
              <CardContent className="py-12 text-center">
                <Lock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Usuarios</h3>
                <p className="text-gray-500">Próximamente</p>
              </CardContent>
            </Card>
          )}

          {activeTab === 'preferencias' && (
            <PreferenciasTab />
          )}

          {activeTab === 'plan' && (
            <Card data-testid="settings-plan">
              <CardContent className="py-12 text-center">
                <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Plan</h3>
                <p className="text-gray-500">Próximamente</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Quote Page Component (no authentication required)
const PublicQuotePage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [acceptData, setAcceptData] = useState({
    approver_name: '',
    approver_email: '',
    terms_accepted: false
  });
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadPublicQuote();
  }, [token]);

  const loadPublicQuote = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getPublicQuote(token);
      setQuote(data);
    } catch (error) {
      console.error('Error loading public quote:', error);
      if (error.response?.status === 403) {
        setError('Link expirado o inválido');
      } else if (error.response?.status === 404) {
        setError('Cotización no encontrada');
      } else {
        setError('Error al cargar la cotización');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await apiService.downloadPublicQuotePDF(token);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Cotizacion-COT-${quote?.quote?.seq || 'N-A'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert('Error al descargar PDF');
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    setAccepting(true);
    try {
      await apiService.acceptPublicQuote(token, acceptData);
      alert('¡Cotización aceptada exitosamente!');
      await loadPublicQuote(); // Refresh to show updated status
      setShowAcceptForm(false);
    } catch (error) {
      alert('Error al aceptar cotización');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setRejecting(true);
    try {
      await apiService.rejectPublicQuote(token, { rejection_reason: rejectionReason });
      alert('Cotización rechazada');
      await loadPublicQuote(); // Refresh to show updated status
      setShowRejectForm(false);
    } catch (error) {
      alert('Error al rechazar cotización');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { quote: quoteData, client, company, can_accept } = quote;

  return (
    <div className="min-h-screen bg-gray-50 public-page-mobile">
      {/* Header */}
      <div className="bg-white shadow public-header-mobile">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Cotización #COT-{quoteData.seq}
              </h1>
              <p className="text-gray-600 mt-1">
                De: {company.name}
              </p>
            </div>
            <div className="text-right">
              <Badge className={
                quoteData.status === 'ACEPTADA' ? 'bg-green-100 text-green-800' :
                quoteData.status === 'RECHAZADA' ? 'bg-red-100 text-red-800' :
                quoteData.status === 'ENVIADA' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }>
                {quoteData.status}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto public-content-mobile">
        {/* Mobile Totals Highlight */}
        <div className="public-totals-mobile show-mobile">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Total de la cotización</p>
            <p className="text-3xl font-bold text-blue-600">
              ${quoteData.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {quoteData.currency}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Válida por {quoteData.validity_days} días
            </p>
          </div>
        </div>
        {/* Quote Summary */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Info */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Cliente</h3>
              <div className="space-y-1 text-gray-600">
                <p className="font-medium">{client.business_name}</p>
                <p>{client.contact_name}</p>
                <p>{client.email}</p>
              </div>
            </div>

            {/* Quote Info */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Información</h3>
              <div className="space-y-1 text-gray-600">
                <div className="flex justify-between">
                  <span>Fecha:</span>
                  <span>{new Date(quoteData.date).toLocaleDateString('es-MX')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Vigencia:</span>
                  <span>{quoteData.validity_days} días</span>
                </div>
                {quoteData.due_date && (
                  <div className="flex justify-between">
                    <span>Vence el:</span>
                    <span className={`font-medium ${
                      new Date(quoteData.due_date) < new Date() 
                        ? 'text-red-600' 
                        : new Date(quoteData.due_date) < new Date(Date.now() + 48 * 60 * 60 * 1000)
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}>
                      {new Date(quoteData.due_date).toLocaleDateString('es-MX')}
                      {new Date(quoteData.due_date) < new Date() && ' (VENCIDA)'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Moneda:</span>
                  <span>{quoteData.currency}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Productos y Servicios</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Producto</th>
                  <th className="text-center py-2">Cantidad</th>
                  <th className="text-right py-2">Precio Unit.</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {quoteData.items.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2">
                      <div>
                        <p className="font-medium">{item.snapshot?.product_name || 'Producto'}</p>
                        <p className="text-sm text-gray-500">{item.snapshot?.category_name}</p>
                      </div>
                    </td>
                    <td className="text-center py-2">{item.quantity}</td>
                    <td className="text-right py-2">
                      ${item.unit_price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-right py-2">
                      ${item.amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Desktop Totals */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 hide-mobile">
          <div className="max-w-sm ml-auto space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${quoteData.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
            </div>
            {quoteData.global_discount_pct > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Descuento ({quoteData.global_discount_pct}%):</span>
                <span>-${((quoteData.subtotal * quoteData.global_discount_pct) / 100)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {quoteData.iva_pct > 0 && (
              <div className="flex justify-between">
                <span>IVA ({quoteData.iva_pct}%):</span>
                <span>${quoteData.iva_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div className="border-t pt-2">
              <div className="flex justify-between text-lg font-bold">
                <span>TOTAL:</span>
                <span>${quoteData.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {quoteData.currency}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Notes */}
        {quoteData.sales_notes && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-3">Notas</h3>
            <p className="text-gray-600">{quoteData.sales_notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* Desktop Actions */}
          <div className="flex flex-wrap gap-4 justify-center hide-mobile">
            {/* Download PDF */}
            <Button onClick={handleDownloadPDF} className="flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Descargar PDF
            </Button>

            {/* Accept Button */}
            {can_accept && (
              <Button 
                onClick={() => setShowAcceptForm(true)}
                className="bg-green-600 hover:bg-green-700 flex items-center"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aceptar Cotización
              </Button>
            )}

            {/* Reject Button */}
            {can_accept && (
              <Button 
                variant="outline"
                onClick={() => setShowRejectForm(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 flex items-center"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rechazar
              </Button>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="public-actions-mobile show-mobile">
            {/* Download PDF */}
            <Button onClick={handleDownloadPDF} className="touch-button w-full" variant="outline">
              <Download className="h-5 w-5 mr-2" />
              Descargar PDF
            </Button>

            {/* Accept Button */}
            {can_accept && (
              <Button 
                onClick={() => setShowAcceptForm(true)}
                className="touch-button w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Aceptar Cotización
              </Button>
            )}

            {/* Reject Button */}
            {can_accept && (
              <Button 
                variant="outline"
                onClick={() => setShowRejectForm(true)}
                className="touch-button w-full border-red-300 text-red-600 hover:bg-red-50"
              >
                <XCircle className="h-5 w-5 mr-2" />
                Rechazar Cotización
              </Button>
            )}
          </div>

          {/* Accept Form */}
          {showAcceptForm && (
            <div className="mt-6 p-4 border rounded-lg bg-green-50">
              <h4 className="font-semibold mb-3">Aceptar Cotización</h4>
              <form onSubmit={handleAccept} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre completo *</label>
                  <input
                    type="text"
                    required
                    value={acceptData.approver_name}
                    onChange={(e) => setAcceptData(prev => ({ ...prev, approver_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={acceptData.approver_email}
                    onChange={(e) => setAcceptData(prev => ({ ...prev, approver_email: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="terms"
                    required
                    checked={acceptData.terms_accepted}
                    onChange={(e) => setAcceptData(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="terms" className="text-sm">
                    Acepto los términos y condiciones de esta cotización *
                  </label>
                </div>
                <div className="flex space-x-2">
                  <Button type="submit" disabled={accepting} className="bg-green-600 hover:bg-green-700">
                    {accepting ? 'Procesando...' : 'Confirmar Aceptación'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAcceptForm(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Reject Form */}
          {showRejectForm && (
            <div className="mt-6 p-4 border rounded-lg bg-red-50">
              <h4 className="font-semibold mb-3">Rechazar Cotización</h4>
              <form onSubmit={handleReject} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Motivo del rechazo (opcional)</label>
                  <textarea
                    rows="3"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Explica brevemente por qué rechazas esta cotización..."
                  />
                </div>
                <div className="flex space-x-2">
                  <Button type="submit" disabled={rejecting} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                    {rejecting ? 'Procesando...' : 'Confirmar Rechazo'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRejectForm(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
        
        {/* In-app browser tip */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            ¿Problemas visualizando? <a 
              href={window.location.href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Abrir en navegador externo
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Terms and Conditions Page
const TermsPage = () => (
  <div className="min-h-screen bg-gray-50 py-12 px-4">
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm p-8 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Términos y Condiciones de Uso — Presuply</h1>
      <p className="text-sm text-gray-500">Versión Beta — Abril 2026</p>
      <div className="space-y-5 text-sm text-gray-700 leading-relaxed">
        <section><h2 className="font-semibold text-gray-900 mb-1">1. Aceptación</h2><p>Al crear una cuenta en Presuply, aceptas estos términos. Si no estás de acuerdo, no uses el servicio.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">2. Descripción del servicio</h2><p>Presuply es una plataforma de gestión de cotizaciones para empresas y emprendedores. Permite crear, enviar y dar seguimiento a cotizaciones profesionales.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">3. Versión Beta</h2><p>Este servicio se encuentra en fase beta. Puede contener errores o interrupciones. Al usarlo, aceptas que el servicio se ofrece &quot;tal como está&quot; durante esta fase.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">4. Tu cuenta</h2><p>Eres responsable de mantener la confidencialidad de tu contraseña y de todas las actividades que ocurran en tu cuenta.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">5. Tus datos</h2><p>La información que ingreses (clientes, productos, cotizaciones) es tuya. No compartimos ni vendemos tu información a terceros.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">6. Uso aceptable</h2><p>No puedes usar Presuply para actividades ilegales, enviar spam o intentar acceder a cuentas de otros usuarios.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">7. Disponibilidad</h2><p>Durante el beta, el servicio puede tener interrupciones sin previo aviso. Haremos nuestro mejor esfuerzo para mantenerlo disponible.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">8. Cambios</h2><p>Podemos modificar estos términos en cualquier momento. Te notificaremos por email si hay cambios importantes.</p></section>
        <section><h2 className="font-semibold text-gray-900 mb-1">9. Contacto</h2><p>Para dudas o soporte: soporte@presuply.com (próximamente)</p></section>
      </div>
    </div>
  </div>
);

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* CRITICAL: Public route MUST be first and outside AuthProvider */}
          <Route path="/q/:token" element={<PublicQuotePage />} />
          <Route path="/terms" element={<TermsPage />} />
          
          {/* All other routes with authentication */}
          <Route path="/*" element={
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
                <Route path="/catalog/categories" element={<ProtectedRoute><Categories /></ProtectedRoute>} />
                <Route path="/catalog/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
                <Route path="/quotes" element={<ProtectedRoute><QuotesList /></ProtectedRoute>} />
                <Route path="/quotes/new" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />
                <Route path="/quotes/:id" element={<ProtectedRoute><QuoteDetail /></ProtectedRoute>} />
                <Route path="/quotes/:id/edit" element={<ProtectedRoute><QuoteEditWrapper /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AuthProvider>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
